import React, { useState, useRef, useEffect, useCallback, useMemo, Suspense } from "react";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import LoginButton from "../../config/LoginButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Profile from "../../config/Profile";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ImportActualModal from "../dialogs/ImportActualModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Typography,
  Link,
  Chip,
  Tooltip,
  Button,
  ButtonGroup,
  Select,
  Skeleton,
  Paper,
  MenuList,
  Stack,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import Http from "../../../services/Http";
import DateFormatter from "../../../hooks/DateFormatter";
import dataService from "../../../services/data.service";
import PlanVersionModal from "../../common/dialogs/PlanVersionModal";
import ConfigureComparisonDialog from "../dialogs/ConfigureComparisonDialog";
import { Icon } from "@iconify/react";
import useToast from "../../../hooks/Toast";
import { handleApiError } from "../../../Utils/errorHandler";
import DiscardChangesDialog from "../dialogs/DiscardChangesDialog";
import SaveAsNewDialog from "../dialogs/SaveAsNewDialog";
import { useHMG } from "../../../HMGContext";
import { useTpxPermissions } from "../../../contexts/TpxPermissionsContext";
import { Menu, MenuItem } from "@mui/material";
import UnDiscardChangesDialog from "../dialogs/UnDiscardChangesDialog";
import MarkCompleteDialog from "../dialogs/MarkCompleteDialog";
import { lazyWithRetry } from "../../../Utils/lazyWithRetry";
import LoadingScreen from "../LoadingScreen";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { getRouteConfig, isFeatureEnabled, getPageType, getPageTitle } from "../../../config/routeConfig";
import { COA_DRAFT_SAVE_EVENTS } from "../../../pages/coa-management/coaDraftSaveEvents";
import CritiqueTemplateDetailsHeader from "./CritiqueTemplateDetailsHeader";
import CritiqueDetailsHeader from "./CritiqueDetailsHeader";
import GetAppIcon from "@mui/icons-material/GetApp";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UserAvatar from "../icons/UserAvatar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import {
  hasConfiguredPlanComparison,
  formatPlanComparisonLabel,
} from "../../../pages/edit-plans/utils/planComparisonLabelUtils";
import { adaptComparisons, resolveComparisonName } from "../../../pages/edit-plans/utils/planComparisonModel";

const CritiqueExportPDFModal = lazyWithRetry(() =>
  import("../dialogs/CritiqueExportPDFModal")
);

const CRITIQUE_DETAILS_DATA_EVENT = "critique-details-data";

// Component for plan name with tooltip
const PlanNameWithTooltip = ({ planName }) => {
  const planNameRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  
  useEffect(() => {
    const checkTruncation = () => {
      if (planNameRef.current) {
        const element = planNameRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };
    
    // Check after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [planName]);
  
  return (
    <Tooltip
      title={planName}
      arrow
      placement="bottom-start"
      disableHoverListener={!isTruncated}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 400,
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
        },
      }}
    >
      <h1 
        ref={planNameRef}
        className="text-2xl leading-[36px] font-bold flex-shrink-0  max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap cursor-default"
      >
        {planName}
      </h1>
    </Tooltip>
  );
};

const Header = ({
  isEdited,
  setIsEdited,
  refreshPageData,
  setRefreshPageData,
  setShowVarianceInHighlights,
  setShowVarianceInRollups,
}) => {
  const [isSearchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null); // Create a ref to access the input
  const location = useLocation();
  const [planDetails, setPlanDetails] = useState(null);
  const [templateData, setTemplateData] = useState(null); // For critique template edit page
  const [createNewPlan, setCreateNewPlan] = useState();
  const [loading, setLoading] = useState(false);
  const [openVersionModal, setOpenVersionModal] = useState(false);
  const [openCreateVersion, setOpenCreateVersion] = useState(false);
  const { id } = useParams();
  
  // Get route configuration based on current pathname
  const routeConfig = getRouteConfig(location.pathname);
  const pageType = getPageType(location.pathname);
  
  // Legacy variables for backward compatibility (can be removed after full migration)
  const isPlanVersion = pageType === 'planVersion';
  const isCOAPage = pageType === 'coa';
  const isCritiqueTemplateEdit = pageType === 'critiqueTemplateEdit';
  const isCritiqueDetails = pageType === 'critiqueDetails';
  const [critiqueDetailsData, setCritiqueDetailsData] = useState(null);
  const [dateFormat, setDateFormat] = useState("MMM d, yyyy");
  const [planDetailsRowData, setPlanDetailsRowData] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDiscardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [isCritiqueDiscardDialogOpen, setCritiqueDiscardDialogOpen] = useState(false);
  const [isCritiqueDetailsDiscardDialogOpen, setCritiqueDetailsDiscardDialogOpen] = useState(false);
  const [isCritiqueCompleteDialogOpen, setCritiqueCompleteDialogOpen] = useState(false);
  const [isSaveAsNewDialogOpen, setSaveAsNewDialogOpen] = useState(false);
  const [planVersionsOptions, setPlanVersionOptions] = useState([]);
  const [planComparisonData, setPlanComparisonData] = useState([]);
  const [isNewVersion, setisNewVersion] = useState(false);
  const { Toast, showToast, closeToast } = useToast();
  const [anchorElResponsive, setAnchorElResponsive] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isCoaDiscardDialogOpen, setCoaDiscardDialogOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
 const [markCompleteModalOpen, setMarkCompleteModalOpen] = useState(false);
 const [modalOpenedFrom, setModalOpenedFrom] = useState(null);
  const [critiqueExportPDFModalOpen, setCritiqueExportPDFModalOpen] = useState(false);
  const [openImportActualModal, setOpenImportActualModal] = useState(false);
  const [copyDataAnchor, setCopyDataAnchor] = useState(null);
  const [importMode, setImportMode] = useState("plan");
  const [draftSaveStatus, setDraftSaveStatus] = useState(null); // null | "saving" | "saved"
  const draftSaveOutcomeRef = useRef(null); // null | "success" | "failed"
 
  const {
    handleSaveNewAction,
    handleSaveAction,
    handleDiscard,
    showHeaderBtn,
    setShowHeaderBtn,
    setHandleResponse,
    saveMappings,
    saveCOAMappingDraft,
    showCoaHeaderBtn,
    setShowCoaHeaderBtn,
    headerActions,
    structure,
    isReadOnly,
    users,
    setUsers,
    fetchUsers,
    setCurrentModalName,
    setHeaderComparisonPlanLabel,
    setPlanComparisons,
    headerComparisonLabels,
    setHeaderComparisonLabels,
    setSelectedComparisonIds,
    selectedHMG,
    setPlanTableRefreshTrigger,
  } = useHMG();

  // Last closed month (header indicator for All Plans & Plan Details)
  const [lastClosedAccountingMonth, setLastClosedAccountingMonth] = useState(null);
  const [isClosedMonthLoading, setIsClosedMonthLoading] = useState(false);
  const showLastClosedMonthInHeader = pageType === "planVersion";

  const closedMonthLabel = useMemo(() => {
    if (!lastClosedAccountingMonth) return null;
    const month = Number(lastClosedAccountingMonth % 100);
    const year = Math.floor(Number(lastClosedAccountingMonth) / 100);
    if (!month || !year || month < 1 || month > 12) return null;
    return dayjs(new Date(year, month - 1, 1)).format("MMMM YYYY");
  }, [lastClosedAccountingMonth]);

  const closedMonthWarningLabel = useMemo(() => {
    if (!lastClosedAccountingMonth) return null;
    const month = Number(lastClosedAccountingMonth % 100);
    const year = Math.floor(Number(lastClosedAccountingMonth) / 100);
    if (!month || !year || month < 1 || month > 12) return null;
    return dayjs(new Date(year, month - 1, 1)).format("MMMM");
  }, [lastClosedAccountingMonth]);

  const isClosedMonthStale = useMemo(() => {
    if (!lastClosedAccountingMonth) return false;
    const month = Number(lastClosedAccountingMonth % 100);
    const year = Math.floor(Number(lastClosedAccountingMonth) / 100);
    if (!month || !year || month < 1 || month > 12) return false;
    const monthEnd = dayjs(new Date(year, month - 1, 1)).endOf("month");
    return dayjs().diff(monthEnd, "day") >= 30;
  }, [lastClosedAccountingMonth]);

  const isClosedMonthWarning = isClosedMonthStale || (!isClosedMonthLoading && !closedMonthLabel);

  useEffect(() => {
    if (!showLastClosedMonthInHeader) return;
    const orgId = dataService.getSelectedHMGId();
    if (!orgId) {
      setLastClosedAccountingMonth(null);
      return;
    }
    // Resolve property-level closed month first (falls back to org-level server-side) when a plan's
    // property is known; otherwise use the org-level value.
    const propertyId = planDetails?.propertyIds?.[0];
    setIsClosedMonthLoading(true);
    // Property-level resolve stays a direct call (property-specific); the org-level get-data path
    // goes through the shared cached/de-duped getter so it isn't fetched separately by the page
    // and header on load (TPX-1856).
    const fetchClosedMonth = propertyId
      ? dataService.getResolvedClosedMonth(orgId, propertyId)
      : dataService.getClosedMonthAccountData(orgId);
    fetchClosedMonth
      .then((data) => {
        setLastClosedAccountingMonth(data?.closedAccountingMonth ?? null);
      })
      .catch(() => setLastClosedAccountingMonth(null))
      .finally(() => setIsClosedMonthLoading(false));
  }, [showLastClosedMonthInHeader, selectedHMG?.id, planDetails?.propertyIds]);

  // Ensure COA header state is cleared when not on COA pages
  useEffect(() => {
    if (pageType !== 'coa') {
      setShowCoaHeaderBtn(false);
    }
  }, [pageType, setShowCoaHeaderBtn]);

  // Reset autosave indicator on navigation so Critique "Autosaved" never appears on COA (or other routes).
  useEffect(() => {
    draftSaveOutcomeRef.current = null;
    setDraftSaveStatus(null);
  }, [location.pathname]);

  // Traditional read-only reasons: plan-level lock or reprocessing.
  const isTraditionalPlanReadOnly = Boolean(planDetails?.readOnly || isReadOnly);
  // Permission-based read-only: user has Plan View but not Plan Edit.
  const { planView, planEdit, versionCreate, settingsView, critiqueComment, critiqueSubmit, critiqueTemplateEdit } = useTpxPermissions();
  const closedMonthStaleTooltipTitle = useMemo(() => {
    const monthLabel = closedMonthWarningLabel || closedMonthLabel;
    if (!monthLabel) return null;
    const prefix = `Accounting actuals last updated through ${monthLabel}.`;
    return settingsView ? `${prefix} Go to Settings to update.` : prefix;
  }, [closedMonthWarningLabel, closedMonthLabel, settingsView]);
  const isPermissionReadOnly = Boolean(planView && !planEdit);
  // Overall read-only used for edit controls (save/discard/etc).
  const isPlanReadOnly = Boolean(isTraditionalPlanReadOnly || isPermissionReadOnly);
  const isCoaReadOnly = Boolean(isReadOnly);
  const saveAsNewVersionHidden =
    isPlanVersion &&
    (planDetails?.locked === true || planDetails?.isLocked === true);
  const saveAsNewVersionDisabled =
    isPlanVersion &&
    (!versionCreate ||
      planDetails?.locked === true ||
      planDetails?.isLocked === true);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setAnchorElResponsive(null);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Add these handler functions for the responsive menu
  const handleOpenResponsiveMenu = (event) => {
    setAnchorElResponsive(event.currentTarget);
  };

  const handleCloseResponsiveMenu = () => {
    setAnchorElResponsive(null);
  };

  // Add this function to determine if we should show items inline or in dropdown
  // Modify this function to determine what displays inline vs in dropdown
  const shouldShowInline = (item) => {
    if (windowWidth > 1366) {
      // Show everything inline at large screens
      return true;
    } else if (windowWidth > 1023) {
      // For 1024-1366, show only up to week chip (as on date)
      return [
        "versionNumber",
        "versionName",
        "planStatus",
        "asOnDate",
        "readOnly",
        // "revenueType",
      ].includes(item);
    } else if (windowWidth > 768) {
      // For 768-1024, show only up to published status
      return ["versionNumber", "versionName", "planStatus"].includes(item);
    }
    // For smaller screens, show only version number and name
    return ["versionNumber", "versionName"].includes(item);
  };

  const handleOpenDialog = () => {
    if (isPermissionReadOnly) return;
    setDialogOpen(true);
    setCurrentModalName("Configure comparison data");
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentModalName(null)
  };
  const routerNavigateTo = useNavigate();

  const allOptions = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Reports", link: "/reports" },
    { title: "Settings", link: "/settings" },
    { title: "User management", link: "/users" },
  ];

  const handleVersionClick = () => {
    // Access-control behavior: disable Version settings only for permission-based view-only.
    // Traditional read-only (plan lock / reprocessing) keeps existing Version settings behavior.
    if (isPermissionReadOnly) return;
    setOpenVersionModal(true);
    setisNewVersion(false);
    setModalOpenedFrom('settings');
    setCurrentModalName("Version settings")
  };

  const handlePlanStatusClick = () => {
    if (isPermissionReadOnly) return;
    setOpenVersionModal(true);
    setisNewVersion(false);
    setModalOpenedFrom('planStatus');
    setCurrentModalName("Version settings");
  };

  const handleReadOnlyClick = () => {
    if (isPermissionReadOnly) return;
    setOpenVersionModal(true);
    setisNewVersion(false);
    setModalOpenedFrom('readOnly');
    setCurrentModalName("Version settings");
  };


  const fetchHeaderData = (showLoading = false) => {
    // Use route config to determine if header data should be fetched
    if (isFeatureEnabled(location.pathname, 'fetchHeaderData')) {
      if (showLoading) {
        setLoading(true);
      }
      setIsEdited(false);
      
      if (isCritiqueTemplateEdit) {
        // Fetch critique template data - Save button starts disabled until user makes changes
        setShowHeaderBtn(false);
        Http.GET(`/critique-templates/${id}/detail`).then((resp) => {
          setTemplateData(resp.data);
          setLoading(false);
        }).catch(() => {
          setLoading(false);
        });
      } else if (isCritiqueDetails) {
        setShowHeaderBtn(false);
        setLoading(false);
        fetchUsers(); // Load watchers for critique presence
      } else {
        // Fetch plan data (existing logic)
        Http.GET(`plan/header-data/${id}`).then((resp) => {
          setPlanDetails(resp.data.plan);
          setPlanVersionOptions(resp.data.versions);
          // TPX-1903: `comparison` may be an array (multi) or a single object (legacy). Normalize to a
          // raw array, keep the first entry for the existing single-comparison render, and publish the
          // canonical list + per-comparison labels for all downstream surfaces.
          const rawComparisons = Array.isArray(resp.data.comparison)
            ? resp.data.comparison
            : resp.data.comparison
              ? [resp.data.comparison]
              : [];
          const configuredRaw = rawComparisons.filter(hasConfiguredPlanComparison);
          setPlanComparisonData(configuredRaw[0] ?? null);
          setPlanComparisons(adaptComparisons({ comparison: configuredRaw }, resp.data.plan));
          const labels = configuredRaw.map((entry, i) => ({
            name: resolveComparisonName(entry, i),
            summary: formatPlanComparisonLabel(entry, resp.data.plan),
          }));
          setHeaderComparisonLabels(labels);
          setHeaderComparisonPlanLabel(labels[0]?.summary ?? null);
          setSelectedComparisonIds(null);
          setLoading(false);
          if (openVersionModal) {
            setOpenVersionModal(false);
          }
          if (openCreateVersion) {
            setOpenCreateVersion(false);
          }
          const hasUnsavedChanges = sessionStorage.getItem("hasUnsavedChanges") === "true";
          if (hasUnsavedChanges) {
            setShowHeaderBtn(true);
          }
        }).catch(() => {
          setLoading(false);
        });
      }
    } else {
      setIsEdited(false);
      setShowHeaderBtn(false);
    }
  };
    
  useEffect(() => {
    // Use route config to determine if header data should be fetched
    if (isFeatureEnabled(location.pathname, 'fetchHeaderData')) {
      if (!createNewPlan && !isDialogOpen) {
        // Only set loading true for initial data fetch
        fetchHeaderData(true);
      } else {
        fetchHeaderData(false);
      }
    } else {
      fetchHeaderData(false);
    }
    
    setHandleResponse({
      setLoading,
      setIsEdited,
      setOpenVersionModal,
      setOpenCreateVersion,
      preserveHeaderBtnState: true,
    });
  }, [id, createNewPlan , refreshPageData]);

  useEffect(() => {
    if (!isCritiqueDetails) {
      setCritiqueDetailsData(null);
      return undefined;
    }

    setCritiqueDetailsData(null);

    const handleCritiqueDetailsData = (event) => {
      setCritiqueDetailsData(event.detail?.critiqueData ?? null);
    };

    window.addEventListener(CRITIQUE_DETAILS_DATA_EVENT, handleCritiqueDetailsData);

    return () => {
      window.removeEventListener(CRITIQUE_DETAILS_DATA_EVENT, handleCritiqueDetailsData);
    };
  }, [id, isCritiqueDetails]);

  const canExportCritiquePdf = useMemo(() => {
    if (!critiqueDetailsData) return false;
    const status = (critiqueDetailsData.status || "")
      .toString()
      .toLowerCase()
      .trim();

    return status === "completed" || status === "overdue_submitted";
  }, [critiqueDetailsData]);

  const critiqueExportPdfTooltip = !critiqueDetailsData
    ? "Export to PDF"
    : canExportCritiquePdf
      ? "Export to PDF"
      : "Export to PDF is available when critique status is Published";

  useEffect(() => {
    if (canExportCritiquePdf) {
      import("../dialogs/CritiqueExportPDFModal").catch(() => {});
    }
  }, [canExportCritiquePdf]);

  // Open "Save and complete" dialog only after EditCritique passes pre-submit validation
  useEffect(() => {
    if (!isCritiqueDetails) return undefined;
    const openCompleteDialog = () => setCritiqueCompleteDialogOpen(true);
    window.addEventListener("critique-details-open-complete-dialog", openCompleteDialog);
    return () => window.removeEventListener("critique-details-open-complete-dialog", openCompleteDialog);
  }, [isCritiqueDetails]);

  // Add this effect to listen for critique template updates
  useEffect(() => {
    // Function to handle critique template updates
    const handleCritiqueTemplateUpdate = (event) => {
      if (isCritiqueTemplateEdit && event.detail && event.detail.templateId === id) {
        // Refresh header data when template is updated
        fetchHeaderData(false);
      }
    };

    window.addEventListener("critique-template-updated", handleCritiqueTemplateUpdate);

    return () => {
      window.removeEventListener("critique-template-updated", handleCritiqueTemplateUpdate);
    };
  }, [id, isCritiqueTemplateEdit]);

  // Listen for critique draft save status (spinner → checkmark → hide after 5s)
  useEffect(() => {
    if (!isCritiqueTemplateEdit) return;

    const handleDraftStarted = () => {
      draftSaveOutcomeRef.current = null;
      setDraftSaveStatus("saving");
    };
    const handleDraftSuccess = () => {
      draftSaveOutcomeRef.current = "success";
      setDraftSaveStatus("saved");
    };
    const handleDraftFailed = () => {
      draftSaveOutcomeRef.current = "failed";
      setDraftSaveStatus(null);
    };
    const handleDraftCompleted = () => {
      // If the request finished without a SUCCESS event, clear "Autosaving…" (do not show "Autosaved").
      if (draftSaveOutcomeRef.current !== "success") {
        setDraftSaveStatus(null);
      }
    };

    window.addEventListener("critique-draft-save-started", handleDraftStarted);
    window.addEventListener("critique-draft-save-completed", handleDraftCompleted);
    window.addEventListener("critique-draft-save-success", handleDraftSuccess);
    window.addEventListener("critique-draft-save-failed", handleDraftFailed);

    return () => {
      window.removeEventListener("critique-draft-save-started", handleDraftStarted);
      window.removeEventListener("critique-draft-save-completed", handleDraftCompleted);
      window.removeEventListener("critique-draft-save-success", handleDraftSuccess);
      window.removeEventListener("critique-draft-save-failed", handleDraftFailed);
    };
  }, [isCritiqueTemplateEdit]);

  // Listen for critique details (instance) draft save status (spinner → checkmark → hide after 5s)
  useEffect(() => {
    if (!isCritiqueDetails) return;

    const handleDraftStarted = () => {
      draftSaveOutcomeRef.current = null;
      setDraftSaveStatus("saving");
    };
    const handleDraftSuccess = () => {
      draftSaveOutcomeRef.current = "success";
      setDraftSaveStatus("saved");
    };
    const handleDraftFailed = () => {
      draftSaveOutcomeRef.current = "failed";
      setDraftSaveStatus(null);
    };
    const handleDraftCompleted = () => {
      if (draftSaveOutcomeRef.current !== "success") {
        setDraftSaveStatus(null);
      }
    };

    window.addEventListener("critique-details-draft-save-started", handleDraftStarted);
    window.addEventListener("critique-details-draft-save-completed", handleDraftCompleted);
    window.addEventListener("critique-details-draft-save-success", handleDraftSuccess);
    window.addEventListener("critique-details-draft-save-failed", handleDraftFailed);

    return () => {
      window.removeEventListener("critique-details-draft-save-started", handleDraftStarted);
      window.removeEventListener("critique-details-draft-save-completed", handleDraftCompleted);
      window.removeEventListener("critique-details-draft-save-success", handleDraftSuccess);
      window.removeEventListener("critique-details-draft-save-failed", handleDraftFailed);
    };
  }, [isCritiqueDetails]);

  // COA mapping + Otelier structure draft save status (same Autosaving / Autosaved pattern as Critique)
  useEffect(() => {
    if (pageType !== "coa") return undefined;

    const handleDraftStarted = () => {
      draftSaveOutcomeRef.current = null;
      setDraftSaveStatus("saving");
    };
    const handleDraftSuccess = () => {
      draftSaveOutcomeRef.current = "success";
      setDraftSaveStatus("saved");
    };
    const handleDraftFailed = () => {
      draftSaveOutcomeRef.current = "failed";
      setDraftSaveStatus(null);
    };
    const handleDraftCompleted = () => {
      if (draftSaveOutcomeRef.current !== "success") {
        setDraftSaveStatus(null);
      }
    };
    const handleDraftClear = () => {
      draftSaveOutcomeRef.current = null;
      setDraftSaveStatus(null);
    };

    window.addEventListener(COA_DRAFT_SAVE_EVENTS.STARTED, handleDraftStarted);
    window.addEventListener(COA_DRAFT_SAVE_EVENTS.COMPLETED, handleDraftCompleted);
    window.addEventListener(COA_DRAFT_SAVE_EVENTS.SUCCESS, handleDraftSuccess);
    window.addEventListener(COA_DRAFT_SAVE_EVENTS.FAILED, handleDraftFailed);
    window.addEventListener(COA_DRAFT_SAVE_EVENTS.CLEAR, handleDraftClear);

    return () => {
      window.removeEventListener(COA_DRAFT_SAVE_EVENTS.STARTED, handleDraftStarted);
      window.removeEventListener(COA_DRAFT_SAVE_EVENTS.COMPLETED, handleDraftCompleted);
      window.removeEventListener(COA_DRAFT_SAVE_EVENTS.SUCCESS, handleDraftSuccess);
      window.removeEventListener(COA_DRAFT_SAVE_EVENTS.FAILED, handleDraftFailed);
      window.removeEventListener(COA_DRAFT_SAVE_EVENTS.CLEAR, handleDraftClear);
      setDraftSaveStatus(null);
    };
  }, [pageType]);

  // Clear Autosaved indicator when user triggers final Save, Submit, or Discard
  useEffect(() => {
    const clear = () => {
      draftSaveOutcomeRef.current = null;
      setDraftSaveStatus(null);
    };
    const events = [
      "critique-details-save",
      "critique-template-save",
      "critique-details-submit-request",
      "critique-details-complete",
      "critique-details-discard",
      "critique-template-discard",
    ];
    events.forEach((evt) => window.addEventListener(evt, clear));
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, clear));
    };
  }, []);

  // Add this effect to listen for data changes
  useEffect(() => {
    // Function to handle data changes from the table
    const handleDataChange = (event) => {
      if (event.detail && event.detail.changed) {
        setShowHeaderBtn(true);
        // Store this state to prevent it from being reset
        sessionStorage.setItem("hasUnsavedChanges", "true");
      }
    };

    // Add event listener for table data changes
    window.addEventListener("table-data-changed", handleDataChange);

    // Check if we had unsaved changes (skip for critique template - it manages its own state)
    const hasUnsavedChanges =
      sessionStorage.getItem("hasUnsavedChanges") === "true";
    const isCritiqueTemplateEdit = location.pathname.includes('/critique/template/') && location.pathname !== '/critique/templates';
    if (hasUnsavedChanges && isFeatureEnabled(location.pathname, 'fetchHeaderData') && !isCritiqueTemplateEdit) {
      setShowHeaderBtn(true);
    }

    return () => {
      window.removeEventListener("table-data-changed", handleDataChange);
    };
  }, [location.pathname, setShowHeaderBtn]);

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    if (query) {
      setSuggestions(
        allOptions.filter((option) =>
          option.title.toLowerCase().includes(query.toLowerCase())
        )
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (link) => {
    if (link === "/settings" && !settingsView) return;
    window.location.href = link;
  };

  const handleSaveClick = async () => {
    if (pageType === 'coa' && showCoaHeaderBtn) {
      // Keep Autosaved visible until user does the final Save.
      setDraftSaveStatus(null);
      if (headerActions?.saveMappings) {
        headerActions?.saveMappings()
      }
    } else {
      try {
        const resp = await handleSaveAction();
        if (resp && resp.status === 200) {
          showToast(
            `'${planDetails?.versionName}' version changes have been saved.`,
            "success"
          );
        } else {
          showToast("Failed to save changes. Please try again.", "error");
        }
      } catch (error) {
        showToast("Failed to save changes. Please try again.", "error");
      }
    }
  };

  const handleCritiqueSave = () => {
    // Trigger save for critique template (preserves current published state)
    // Keep Autosaved visible until user does the final Save.
    setDraftSaveStatus(null);
    window.dispatchEvent(new CustomEvent('critique-template-save'));
  };

  const handleCritiqueSettingsClick = () => {
    // Trigger settings click for critique template
    window.dispatchEvent(new CustomEvent('critique-template-settings-click'));
  };

  const handleCritiqueDiscardClick = async () => {
    try {
      const resp = await Http.DELETE(`/critique-templates/${id}/draft`);
      if (resp && (resp.status === 200 || resp.status === 204)) {
        showToast("Changes discarded", "success");
        setShowHeaderBtn(false);
        setCritiqueDiscardDialogOpen(false);
        window.dispatchEvent(new CustomEvent("critique-template-discard", { detail: { templateId: id } }));
      } else {
        showToast("Failed to discard changes. Please try again.", "error");
      }
    } catch (error) {
      handleApiError(error, showToast, "Failed to discard changes. Please try again.");
    }
  };

  const handleCancelCritiqueDiscard = () => {
    setCritiqueDiscardDialogOpen(false);
  };

  const handleCritiqueDetailsDiscardClick = async () => {
    try {
      const resp = await Http.DELETE(`/critique/${id}/draft`);
      if (resp && (resp.status === 200 || resp.status === 204)) {
        showToast("Changes discarded", "success");
        setShowHeaderBtn(false);
        setCritiqueDetailsDiscardDialogOpen(false);
        window.dispatchEvent(new CustomEvent("critique-details-discard", { detail: { critiqueId: id } }));
      } else {
        showToast("Failed to discard changes. Please try again.", "error");
      }
    } catch (error) {
      handleApiError(error, showToast, "Failed to discard changes. Please try again.");
    }
  };

  const handleCancelCritiqueDetailsDiscard = () => {
    setCritiqueDetailsDiscardDialogOpen(false);
  };

  const handlePlanDiscardClick = async () => {
    try {
      const resp = await handleDiscard();
      if (resp && resp.status === 200) {
        showToast(`${planDetails?.versionName} changes discarded`, "success");
      } else {
        showToast("Failed to discard changes. Please try again.", "error");
      }
    } catch (error) {
      showToast("Failed to discard changes. Please try again.", "error");
    }
  };

  const handleCoaDiscardClick = async () => {
    if (headerActions?.handleDiscardClick) {
      headerActions?.handleDiscardClick()
    }
  };

  const handleCancelDiscard = () => {
    setDiscardDialogOpen(false);
  };

  const handleCancelCoaDiscard = () => {
    setCoaDiscardDialogOpen(false)
  }

  const handleSaveAsNewVersionClick = async () => {
    if (saveAsNewVersionDisabled) return;
    // setSaveAsNewDialogOpen(true);
    setOpenCreateVersion(true);
    setisNewVersion(true);
    setModalOpenedFrom(null);
    setCurrentModalName("Save as new version");
    const resp = await handleSaveNewAction();
  };

  const toggleSearch = () => {
    setSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchQuery("");
      setSuggestions([]);
    }
  };
  const stylesMap = {
    budget: { color: "#E02DA4", backgroundColor: "#E02DA429" },
    forecast: { color: "#E9750F", backgroundColor: "#E9750F29" },
    "what if": { color: "#169FA9", backgroundColor: "#169FA929" },
    draft: { color: "#637381", backgroundColor: "#919EAB29" },
    published: { color: "#118D57", backgroundColor: "#22C55E29" },
    final: { color: "#118D57", backgroundColor: "#22C55E29" },
  };
  const style = stylesMap[planDetails?.planStatus.toLowerCase()] || "";

  const [anchorEl, setAnchorEl] = useState(null);
 

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [selectedOption, setSelectedOption] = useState("");

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  // const [nextPath, setNextPath] = useState(null);
  // const [isUnDiscardDialogOpen, setUnDiscardDialogOpen] = useState(false);
  
  // useEffect(() => {
  //   const handleBeforeUnload = (event) => {
  //     if (showHeaderBtn) {
  //       event.preventDefault();
  //       event.returnValue = ""; // This is required for Chrome
  //     }
  //   };

  //   const handleNavigation = (event) => {
  //     // Only handle links, not other clicks
  //     if (!event.target.closest("a")) return;

  //     const targetHref = event.target.closest("a").getAttribute("href");
  //     if (!targetHref) return;

  //     // Don't block navigation within the same plan version
  //     const currentId = location.pathname.split("/").pop();
  //     const targetPath = targetHref.split("/");
  //     const targetId = targetPath[targetPath.length - 1];

  //     if (showHeaderBtn && targetId !== currentId) {
  //       event.preventDefault();
  //       setNextPath(targetHref);
  //       setUnDiscardDialogOpen(true); // Use the new dialog instead
  //     }
  //   };

  //   const handlePopState = () => {
  //     if (showHeaderBtn) {
  //       // Prevent the browser from navigating back
  //       window.history.pushState(null, null, location.pathname);
  //       setUnDiscardDialogOpen(true); // Use the new dialog instead
  //     }
  //   };

  //   // Add event listeners
  //   window.addEventListener("beforeunload", handleBeforeUnload);
  //   document.addEventListener("click", handleNavigation, true);
  //   window.addEventListener("popstate", handlePopState);

  //   // Clean up event listeners
  //   return () => {
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //     document.removeEventListener("click", handleNavigation, true);
  //     window.removeEventListener("popstate", handlePopState);
  //   };
  // }, [showHeaderBtn, location.pathname]);

  // const handleCancelUnDiscardDialog = () => {
  //   setUnDiscardDialogOpen(false);
  //   setNextPath(null); // Reset navigation attempt
  // };

  // const handleSaveAndNavigate = async () => {
  //   try {
  //     const resp = await handleSaveAction();
  //     if (resp && resp.status === 200) {
  //       showToast(
  //         `'${planDetails?.versionName}' version changes have been saved.`,
  //         "success"
  //       );

  //       // Clear the unsaved changes flag
  //       sessionStorage.removeItem("hasUnsavedChanges");
  
  //       // Update the state to reflect that there are no unsaved changes
  //       setShowHeaderBtn(false);
  
  //       // Navigate after successful save and a short delay to ensure toast is visible
  //       setTimeout(() => {
  //         if (nextPath) {
  //           window.location.href = nextPath;
  //         }
  //       }, 300);
  //     } else {
  //       showToast("Failed to save changes. Please try again.", "error");
  //     }
  //   } catch (error) {
  //     showToast("Failed to save changes. Please try again.", "error");
  //   } finally {
  //     setUnDiscardDialogOpen(false);
  //     setNextPath(null); // Clear the navigation path regardless of success/failure
  //   }
  // };

  // const handleDiscardAndNavigate = async () => {
  //   try {
  //     const resp = await handleDiscard();
  //     if (resp && resp.status === 200) {
  //       showToast(`${planDetails?.versionName} changes discarded`, "success");
  
  //       // Clear the unsaved changes flag
  //       sessionStorage.removeItem("hasUnsavedChanges");
  
  //       // Update the state to reflect that there are no unsaved changes
  //       setShowHeaderBtn(false);
  
  //       // Navigate after successful discard
  //       if (nextPath) {
  //         window.location.href = nextPath;
  //       } else {
  //         window.history.back();
  //       }
  //     } else {
  //       showToast("Failed to discard changes. Please try again.", "error");
  //     }
  //   } catch (error) {
  //     showToast("Failed to discard changes. Please try again.", "error");
  //   } finally {
  //     setUnDiscardDialogOpen(false);
  //     setNextPath(null); // Clear the navigation path regardless of success/failure
  //   }
  // };

  const handleDueDateClick = () => {
    if (isPermissionReadOnly) return;
    setOpenVersionModal(true);
    setisNewVersion(false);
    setModalOpenedFrom('dueDate');
    setCurrentModalName("Version settings");
};

  const handleCopyDataOpen = (event) => {
    if (isPermissionReadOnly) return;
    setCopyDataAnchor(event?.currentTarget ?? null);
  };

  const handleCopyDataClose = () => {
    setCopyDataAnchor(null);
  };

  const handleCopyDataSelect = (nextMode) => {
    setImportMode(nextMode);
    setCopyDataAnchor(null);
    setOpenImportActualModal(true);
    setCurrentModalName(nextMode === "actuals" ? "Copy data from actuals" : "Copy data from plan");
  };

  const handleSuccessfulUpdate = useCallback(() => {
    fetchHeaderData();
  }, []);

  const handleImportJobComplete = useCallback(() => {
    setRefreshPageData(prev => !prev);
    setPlanTableRefreshTrigger(new Date());
    fetchHeaderData();
  }, [setRefreshPageData, setPlanTableRefreshTrigger]);


  const getDueDateStyles = (dateStr) => {
    const dateStrFormatted = String(dateStr); // ensure it's a string
    const formattedDateStr = `${dateStrFormatted.slice(0, 4)}-${dateStrFormatted.slice(4, 6)}-${dateStrFormatted.slice(6, 8)}`;

    const today = new Date();
    const dueDate = new Date(formattedDateStr);

    const diffInDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) {
      return { color: "#B71D18", backgroundColor: "#FF563029" }; // overdue
    } else if (diffInDays <= 10) {
      return { color: "#B76E00", backgroundColor: "#FFAB0029" }; // reaching soon
    } else {
      return { color: "#006C9C", backgroundColor: "#00B8D929" }; // enough time
    }
  };

  const formatDate = (dateString) => {
    const year = dateString?.substring(0, 4);
    const month = dateString?.substring(4, 6);
    const day = dateString?.substring(6, 8);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const today = new Date();
  const dueDate = planDetails?.asOnDate ? new Date(formatDate(planDetails?.asOnDate.toString())) : "N/A";
  const completedDateStr = planDetails?.completedDateNumber?.toString();
  const year = completedDateStr?.substring(0, 4);
  const month = completedDateStr?.substring(4, 6);
  const day = completedDateStr?.substring(6, 8);
  const completedDate = new Date(year, month - 1, day); 
  const diffInDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  // Check if it was overdue when completed (red style condition)
  const wasOverdue =  dueDate < completedDate

  const formattedDueDate = planDetails?.asOnDate ? formatDate(planDetails?.asOnDate.toString()) : "N/A";
  const formattedCompletedDate = formatDate(planDetails?.completedDateNumber?.toString());

  const tooltipContent =  (
    <div>
      <div className="text-white text-xs leading-[18px]">
       <span className="text-bold"> Due date:</span> <span className="!font-normal">{formattedDueDate}</span>
      </div>
      <div className={`text-xs leading-[18px]" ${wasOverdue ? "text-[#FFAB00]" : " text-[#22C55E]"}`}>
      <span className="text-bold">Completed: </span>  <span className="!font-normal">{formattedCompletedDate}</span>
      </div>
    </div>
  );

  const maxDisplay = 4;
  const displayedUsers = Array.isArray(users) ? users.slice(0, maxDisplay) : [];
  const remainingUsers = Array.isArray(users) ? users.slice(maxDisplay) : [];
  const remainingCount = remainingUsers.length; 
   const StatusDot = ({ active }) => (
     <div className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${
      active === true ? 'bg-[#22C55E]' : 'bg-[#FFAB00]'
     }`} />
   );
   
   const OverflowTooltipContent = ({ remainingUsers }) => (
     <Box sx={{ p: 1, maxWidth: 250 }} className="!p-0 overflow-y-auto max-h-[184px]">
       <Stack className="space-y-2">
         {remainingUsers.map((user) => (
           <Stack key={user.id} direction="row" spacing={1} alignItems="center" className="!py-[3px]" >
             <Box className="relative">
              <UserAvatar
                name={user.fullName || "user"}
                sx={{
                  width: 18,
                  height: 18,
                  fontSize: 8,
                  cursor: "pointer",
                }}
              />
               <div className={`absolute -bottom-[1px] -right-0 w-[6px] h-[6px] rounded-full border border-white ${
                 user.active === true  ? 'bg-[#22C55E]' : 'bg-[#FFAB00]'
               }`} />
             </Box>
             <Typography variant="body2" fontWeight="medium" className="!text-xs text-[#637381]">
               {user.fullName || "user"}
             </Typography> 
           </Stack>
         ))}
       </Stack>
     </Box>
   );

  const renderActiveUserAvatars = () => (
    <div className="flex items-center space-x-0.5">
      {displayedUsers.map((user, index) => (
        <Tooltip
          key={index}
          title={user.fullName || "user"}
          arrow
          placement="top"
          slotProps={{ tooltip: { sx: { cursor: "default" } } }}
        >
          <div key={user.id} className="relative inline-block">
            <UserAvatar
              name={user.fullName || "user"}
              sx={{
                width: 24,
                height: 24,
                fontSize: 12,
                cursor: "default",
              }}
            />
            <StatusDot active={user.active} />
          </div>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <Tooltip
            title={
              <Box className="tpx-tooltip-long-list">
                <OverflowTooltipContent remainingUsers={remainingUsers} />
              </Box>
            }
            arrow
            placement="bottom-end"
            disableHoverListener
            disableFocusListener
            disableTouchListener
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "white",
                  border: "1px solid #E2E5E970",
                  borderRadius: "4px",
                  minWidth: "170px",
                  boxShadow: "-20px 20px 40px 4px #919EAB3D",
                  "& .MuiTooltip-arrow": {
                    boxShadow: "-20px 20px 40px 4px #919EAB3D",
                    "&::before": {
                      backgroundColor: "white",
                      border: "1px solid #E2E5E970",
                    },
                  },
                },
              },
            }}
          >
            <span
              className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-[#637381] font-medium text-xs hover:scale-105 transition-transform cursor-pointer"
              onClick={() => setTooltipOpen(!tooltipOpen)}
            >
              +{remainingCount}
            </span>
          </Tooltip>
        </ClickAwayListener>
      )}
    </div>
  );

  return (
    (<div className="w-full left-0 bg-white z-30 sticky top-0">
      <Toast />
      <div className="container mx-auto max-w-[1920px]">
        <header className="bg-white p-2 px-6  z-40 border-b-2 border-transparent relative h-[68px] flex items-center">
          <div
            className={`
        absolute  left-0 w-full bg-white z-50
        transition-transform duration-[300ms] ease-in-out
        ${isSearchVisible ? "translate-y-0 top-0" : "-translate-y-full -top-6"}
      `}
          >
            <div className="w-full p-5 shadow">
              <TextField
                variant="outlined"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleInputChange}
                autoFocus
                fullWidth
                size="small"
                inputRef={inputRef}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={toggleSearch}>
                      <ClearIcon sx={{ width: "16px", height: "16px" }} />
                    </IconButton>
                  ),
                }}
              />
              {suggestions.length > 0 && (
                <ul
                  className="absolute z-20 bg-white border border-gray-300 rounded-md shadow-lg w-[98%] p-2 mt-4"
                  style={{
                    top: inputRef.current
                      ? inputRef.current.offsetHeight + 5
                      : "0",
                  }}
                >
                  {suggestions.map((option, index) => (
                    <li
                      key={`${option.title}_${index}`}
                      className="p-2 cursor-pointer rounded-lg hover:bg-gray-100 hover:border-2 hover:border-dotted hover:border-slate-500"
                      onClick={() => handleSuggestionClick(option.link)}
                    >
                      {option.title}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="flex items-center w-full">
            {isFeatureEnabled(location.pathname, 'showLoadingSkeleton') && loading ? (
              // Skeleton loading state
              (<Box
                display="flex"
                gap={"2px"}
                flexDirection={"column"}
                width="100%"
              >
                <Box sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}>
                  <Skeleton variant="text" width={180} height={22} />
                </Box>
                {windowWidth > 1365 ? (
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    alignItems={"center"}
                    height={24}
                    className="!gap-[8px] mt-2"
                  >
                    <Skeleton variant="rounded" width={40} height={24} />
                    <Skeleton variant="rounded" width={200} height={36} />
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={100} height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={120} height={24} />
                    <Skeleton variant="rounded" width={30} height={30} />
                    <Skeleton variant="rounded" width={100} height={30} />
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection={"row"}
                    alignItems={"center"}
                    height={24}
                    className="!gap-[8px] mt-2"
                  >
                    <Skeleton variant="rounded" width={40} height={24} />
                    <Skeleton variant="rounded" width={200} height={36} />
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                  </Box>
                )}
              </Box>)
            ) : isFeatureEnabled(location.pathname, 'showBreadcrumbs') ? (
              isCritiqueDetails ? (
                <CritiqueDetailsHeader
                  critiqueData={critiqueDetailsData}
                  loading={!critiqueDetailsData}
                  showReadOnlyLabel={!critiqueComment}
                />
              ) : isCritiqueTemplateEdit ? (
                <CritiqueTemplateDetailsHeader
                  templateData={templateData}
                  showHeaderBtn={showHeaderBtn}
                  onSettingsClick={handleCritiqueSettingsClick}
                  showReadOnlyLabel={!critiqueTemplateEdit}
                />
              ) : (
              <Box display="flex" gap={"2px"} flexDirection={"column"}>
                <Box
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <Breadcrumbs
                    separator="•"
                    sx={{
                      "& .MuiBreadcrumbs-separator": {
                        fontSize: "26px",
                        margin: "0 16px",
                        display: "inline-block",
                        lineHeight: "0",
                      },
                    }}
                    aria-label="breadcrumb"
                  >
                    <Link
                      underline="hover"
                      color="inherit"
                      style={{ cursor: "pointer" }}
                      // onClick={() => {
                      //   if (showHeaderBtn) {
                      //     setNextPath("/");
                      //     setUnDiscardDialogOpen(true);
                      //   } else {
                      //     routerNavigateTo("/");
                      //   }
                      // }}
                      onClick={() => {
                          routerNavigateTo("/");
                      }}
                      className="!text-sm !leading-[22px]"
                    >
                      All plans
                    </Link>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: "14px" }}
                      className="!leading-[22px]"
                    >
                      {planDetails &&
                      dataService.getPropertyById(planDetails?.propertyIds[0])
                        ? dataService.getPropertyById(
                            planDetails?.propertyIds[0]
                          ).name
                        : "NA"}{" "}
                      |{" "}
                      {planDetails?.planType[0].toUpperCase() +
                        planDetails?.planType.substring(1)}{" "}
                      | {planDetails?.fiscalYear}
                    </Typography>
                  </Breadcrumbs>
                </Box>
                <Box
                  display="flex"
                  flexDirection={"row"}
                  alignItems={"center"}
                  height={24}
                  sx={{
                    "& .MuiChip-root": {
                      borderRadius: "6px",
                      fontWeight: 700,
                    },
                  }}
                  className="!gap-[8px]"
                >
                  <Chip
                    size="small"
                    className="!text-[#637381] !bg-[#919EAB] !bg-opacity-[16%]"
                    label={`V${planDetails?.versionNumber}`}
                  />
                  <div className="flex items-center !relative">
                    <Select
                      value={selectedOption}
                      onChange={handleChange} 
                      onClose={(e)=>setIsSelectOpen(false)}
                      onOpen={(e)=>setIsSelectOpen(true)}
                      displayEmpty
                      open={isSelectOpen || false}
                      size="small"
                      sx={{
                        color: "",
                        height: 36,
                        maxWidth: 285,
                        overflow: "hidden",
                        // marginLeft: 1,
                        "&. MuiOutlinedInput-input": {
                          paddingRight: "0px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          paddingRight: "8px !important",
                          paddingLeft: "5px !important",
                        },
                      }}
                      renderValue={() => {
                        const planName = planDetails?.versionName || "";
                        return (
                          <div className="flex items-center gap-2 max-w-[300px] overflow-hidden">
                            <PlanNameWithTooltip planName={planName} />
                          </div>
                        );
                      }}
                      IconComponent={(props) => (
                        <span className="cursor-pointer hover:bg-[#F6F7F8] min-w-[20px] min-h-[20px] rounded-full flex justify-center items-center" >
                          <IconButton
                              onClick={() => {
                                setIsSelectOpen(true)
                              }}
                              sx={{
                                padding: 0,
                                '&:hover': {
                                  backgroundColor: '#F6F7F8',
                                },
                              }}
                            >
                          <Icon
                            icon="solar:alt-arrow-down-line-duotone"
                            width={16}
                            height={16}
                            style={{ color: "#637381" }}
                            className=""
                          />
                          </IconButton>
                      </span>
                      )}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        disableScrollLock: true,
                      }}
                    >
                      {planVersionsOptions &&
                        planVersionsOptions
                          .filter((option) => option.value != planDetails?.id)
                          .map((option, index) => {
                            return (
                              <MenuItem
                                key={`${option.id}_${index}`}
                                className="!bg-white"
                                onClick={() => {
                                  const currentUrl = window.location.href; // Get current URL
                                  const urlParts = currentUrl.split("/"); // Split by "/"
                                  urlParts[urlParts.length - 1] = option.value; // Replace last part with option.value
                                  const newUrl = urlParts.join("/"); // Reconstruct URL

                                  window.open(newUrl, "_blank"); // Open new tab with modified URL
                                  handleClose();
                                }}
                                sx={{
                                  display: "flex",
                                  gap: 1, // Adds space between elements
                                  height: "30px !important",
                                  marginBottom: "4px",
                                  "&:hover": {
                                    backgroundColor: "#f0f0f0 !important", // Hover effect
                                  },
                                }}
                              >
                                {/* Version Number */}
                                <Box
                                  display="flex"
                                  flexDirection={"row"}
                                  alignItems={"center"}
                                  height={24}
                                  sx={{
                                    "& .MuiChip-root": {
                                      borderRadius: "6px",
                                      fontWeight: 700,
                                    },
                                  }}
                                  className="2xl:!gap-[8px] gap-[6px]"
                                >
                                  <Chip
                                    size="small"
                                    className="!text-[#637381] !bg-[#919EAB] !bg-opacity-[16%]"
                                    label={`V${option.versionNumber}`}
                                  />
                                </Box>

                                <div className="text-xs">{option.versionName}</div>

                                {/* Plan Status */}
                                <Box
                                  display="flex"
                                  flexDirection={"row"}
                                  alignItems={"center"}
                                  height={24}
                                  sx={{
                                    "& .MuiChip-root": {
                                      borderRadius: "6px",
                                      fontWeight: 700,
                                    },
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    sx={{
                                      ...(stylesMap[
                                        option?.planStatus.toLowerCase()
                                      ] || stylesMap.default),
                                    }}
                                    label={`${
                                      option?.planStatus[0].toUpperCase() +
                                      option?.planStatus.substring(1)
                                    }`}
                                  />
                                </Box>
                              </MenuItem>
                            );
                          })}
                      {planVersionsOptions.filter(
                        (option) => option.value != planDetails?.id
                      )?.length == 0 && (
                        <MenuItem
                          key="no-data"
                          className="!bg-white"
                          sx={{
                            display: "flex",
                            gap: 1, // Adds space between elements
                            minHeight: "30px !important",
                          }}
                        >
                          <div className="text-xs leading-[18px] text-[#919EAB]">
                            No other plan versions available
                          </div>
                        </MenuItem>
                      )}
                    </Select>
                  </div>
                  {shouldShowInline("planStatus") && (
                    <Box>
                      {planDetails?.planStatus === "Published" ? (
                        <Tooltip
                          title="Used by default in reporting when there is no prompt for a specific plan."
                          arrow
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 165,
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                              },
                            },
                          }}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -5],
                                },
                              },
                            ],
                          }}
                        >
                          <Chip
                            size="small"
                            sx={{
                              ...style,
                              "&:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                              "&.MuiChip-clickable:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                            }}
                            disabled={isPermissionReadOnly}
                            className={isPermissionReadOnly ? "!cursor-not-allowed" : "!cursor-pointer"}
                            onClick={isPermissionReadOnly ? undefined : handlePlanStatusClick}
                            label={`${
                              planDetails?.planStatus[0].toUpperCase() +
                              planDetails?.planStatus.substring(1)
                            }`}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          sx={{
                            ...style,
                            "&:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                            "&.MuiChip-clickable:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                          }}
                          disabled={isPermissionReadOnly}
                          className={isPermissionReadOnly ? "!cursor-not-allowed" : "!cursor-pointer"}
                          onClick={isPermissionReadOnly ? undefined : handlePlanStatusClick}
                          label={`${
                            planDetails?.planStatus[0].toUpperCase() +
                            planDetails?.planStatus.substring(1)
                          }`}
                        />
                      )}
                    </Box>
                  )}
                  {shouldShowInline("asOnDate") && (
                     planDetails?.asOnDate  ? (
                       <Box
                       sx={{
                           "& .MuiChip-root": {
                               backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                               color: planDetails?.completedDateNumber ? "#118D57" : getDueDateStyles(planDetails?.asOnDate).color,
                               borderRadius: "6px",
                               fontWeight: "700",
                               cursor: "pointer"
                           },
                       }}
                       >
                       {planDetails?.completedDateNumber ? 
                           <Tooltip
                                       title={tooltipContent}
                                       arrow
                                       placement="top"
                                       slotProps={{
                                       tooltip: {
                                           sx: {
                                           maxWidth: 165,
                                           whiteSpace: "normal",
                                           wordWrap: "break-word",
                                           },
                                       },
                                       }}
                                       PopperProps={{
                                       modifiers: [
                                           {
                                           name: "offset",
                                           options: {
                                               offset: [0, -5],
                                           },
                                           },
                                       ],
                                       }}
                                   >
                                       <Chip
                                           size="small"
                                           className=""
                                           disabled={isPermissionReadOnly}
                                           onClick={isPermissionReadOnly ? undefined : () => handleDueDateClick()} 
                                          // onClick={handleVersionClick}
                                           label={planDetails?.completedDateNumber ? "Completed" : `Due ${DateFormatter({ value: planDetails?.asOnDate, format: "MMM d, yyyy", outputFormat: "string", }) || "N/A"}`}
                                           icon={
                                            planDetails?.completedDateNumber ? wasOverdue ? (
                                                                        <Icon
                                                                        icon="solar:danger-triangle-bold-duotone"
                                                                        width={16}
                                                                        height={16}
                                                                        style={{ color: "#B76E00" }}
                                                                      />
                                                                      ) : (
                                                                        <Icon
                                                                        icon="solar:check-circle-bold-duotone"
                                                                        width={16}
                                                                        height={16}
                                                                        style={{ color: "#118D57" }}
                                                                      />
                                                                      )  :
                                          //      <AccessAlarmIcon
                                          //      width={"16px"}
                                          //      height={"16px"}
                                          //      color={getDueDateStyles(planDetails?.asOnDate).color}
                                          //      className='!w-4 !h-4'
                                          //  />
                                            <Icon
                                              icon="solar:alarm-bold-duotone"
                                              width={16}
                                              height={16}
                                              color={getDueDateStyles(planDetails?.asOnDate).color}
                                            />
                                           }
                                           sx={{
                                               ".MuiChip-label": {
                                                   paddingLeft : "6px",
                                                   paddingRight: "6px"
                                               },
                                                "&:hover": {
                                                  backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                                },
                                                "&.MuiChip-clickable:hover": {
                                                  backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                                },
                                           }}
                                       />
                                   </Tooltip>
                       : 
                       <Chip
                               size="small"
                               className=""
                               disabled={isPermissionReadOnly}
                               onClick={isPermissionReadOnly ? undefined : () => handleDueDateClick()} 
                              // onClick={handleVersionClick}
                               label={`Due ${DateFormatter({ value: planDetails?.asOnDate, format: "MMM d, yyyy", outputFormat: "string", }) || "N/A"}`}
                               icon={
                              //      <AccessAlarmIcon
                              //      width={"16px"}
                              //      height={"16px"}
                              //      color={getDueDateStyles(planDetails?.asOnDate).color}
                              //      className='!w-4 !h-4'
                              //  />
                                <Icon
                                  icon="solar:alarm-bold-duotone"
                                  width={16}
                                  height={16}
                                  color={getDueDateStyles(planDetails?.asOnDate).color}
                                />
                               }
                               sx={{
                                   ".MuiChip-label": {
                                       paddingLeft : "6px",
                                       paddingRight: "6px"
                                   },
                                   "&:hover": {
                                     backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                   },
                                   "&.MuiChip-clickable:hover": {
                                     backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                   },
                               }}
                           />
                       }
                        </Box>) : (<></>
                   )

                  )}
                    {/* Icon and hover */}
                    {planDetails?.sourceName && (
                      <Tooltip
                        title={
                          <div>
                            <div className="!text-[12px] font-medium leading-[18px]">
                              Originally created in {planDetails?.sourceName} (Last edited in {
                                new Date(planDetails?.sourceModifiedOn).getTime() < new Date(planDetails?.modifiedOn).getTime()
                                  ? "Trueplan"
                                  : planDetails?.sourceName
                              } on {DateFormatter({
                                value: new Date(planDetails?.modifiedOn),
                                showTimeAgo: false,
                                outputFormat: "string"
                              })})
                            </div>
                            <div className="!text-[10px] leading-[14px]">
                              Note: Some logic or drivers were adjusted when brought into TruePlan.
                            </div>
                          </div>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 200,
                              whiteSpace: "normal",
                              wordWrap: "break-word",
                            },
                          },
                        }}
                      >
                        <span className="flex items-center text-[#637381] text-[10px] font-normal cursor-pointer">
                          <CallReceivedIcon
                            sx={{
                              width: "18px",
                              height: "18px",
                              color: "#637381",
                            }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  {/* {shouldShowInline("revenueType") && (
                    <Box
                      sx={{
                        "& .MuiChip-root": {
                          backgroundColor: "#2a5ba229",
                          color: "#0F3C7E",
                        },
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${
                          planDetails?.revenueType[0].toUpperCase() +
                          planDetails?.revenueType.substring(1)
                        }`}
                      />
                    </Box>
                  )} */}
                  {shouldShowInline("readOnly") && (planDetails?.readOnly || isReadOnly) && (
                     <Box
                     sx={{
                       "& .MuiChip-root": {
                         backgroundColor: "#919EAB29",
                         color: "#637381",
                         borderRadius: "6px",
                         fontWeight: "700"
                       },
                       "& .MuiChip-root:hover, & .MuiChip-root.MuiChip-clickable:hover": {
                         backgroundColor: "#919EAB29",
                       },
                     }}
                   >
                    <Chip
                      size="small"
                      label={"Read only"}
                      disabled={isPermissionReadOnly}
                      className={isPermissionReadOnly ? "!cursor-not-allowed" : "!cursor-pointer"}
                      onClick={isPermissionReadOnly ? undefined : handleReadOnlyClick}
                      sx={{ "&:hover": { backgroundColor: "#919EAB29" }, "&.MuiChip-clickable:hover": { backgroundColor: "#919EAB29" } }}
                    />
                    </Box>
                  )}
                  {/* {shouldShowInline("autoRefresh") && (
                    <div className="flex items-center gap-[6px]">
                      <div
                        className={`w-[10px] h-[10px] !m-[2px] ${
                          planDetails?.autoRefreshRevDaily
                            ? "bg-[#22C55E]"
                            : "bg-[#c53d22]"
                        } rounded-full`}
                      />
                      <div className="text-[12px] leading-[20px] 2xl:text-[13px]">
                        {planDetails?.autoRefreshRevDaily
                          ? "Auto refresh rev on"
                          : "Auto refresh rev off"}
                      </div>
                    </div>
                  )} */}
                  <div className="space-x-2 custome_display">
                    {shouldShowInline("settings") && planDetails && (
                      <>
                        <Tooltip
                          title="Version settings"
                          arrow
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#212B36",
                                "& .MuiTooltip-arrow": {
                                  color: "#212B36",
                                },
                              },
                            },
                          }}
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -9], // Adjusts tooltip closer to the icon
                                },
                              },
                            ],
                          }}
                        >
                          <span>
                            <IconButton
                              className="border bg-white p-1.5"
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "8px",
                                borderColor: "#919EAB52",
                              }}
                              sx={{
                                border: "1px solid #D1D5DB",
                                borderRadius: 1,
                                padding: "4px",
                                bgcolor: isPermissionReadOnly ? "transparent" : undefined,
                                opacity: isPermissionReadOnly ? 0.5 : 1,
                                pointerEvents: isPermissionReadOnly ? "none" : "auto",
                              }}
                              disabled={isPermissionReadOnly}
                              onClick={handleVersionClick}
                            >
                              <Icon
                                icon="solar:settings-bold-duotone"
                                width={18}
                                height={18}
                                style={{ color: isPermissionReadOnly ? "#BDBDBD" : "#212B36" }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {showLastClosedMonthInHeader && (
                          <Tooltip
                            title={
                              isClosedMonthLoading
                                ? "Loading last closed month..."
                                : closedMonthLabel
                                  ? isClosedMonthStale
                                    ? closedMonthStaleTooltipTitle
                                    : `Last closed month: ${closedMonthLabel}`
                                  : "Last closed month: Not available"
                            }
                            arrow
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: { fontSize: "12px", maxWidth: 320 },
                              },
                            }}
                            PopperProps={{
                              modifiers: [
                                { name: "offset", options: { offset: [0, -9] } },
                              ],
                            }}
                          >
                            <Box
                              className="border bg-white"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 30,
                                height: 30,
                                borderRadius: "8px",
                                border: "1px solid #D1D5DB",
                                backgroundColor: isClosedMonthWarning ? "#FFF7E6" : "#E6FCFF",
                                cursor: "default",
                              }}
                            >
                              {isClosedMonthWarning ? (
                                <WarningRoundedIcon sx={{ width: 18, height: 18, color: "#FFAB00" }} />
                              ) : (
                                <InfoOutlinedIcon sx={{ width: 18, height: 18, color: "#00B8D9" }} />
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {shouldShowInline("import-plan") && (
                      <>
                        <Tooltip
                          title="Copy data"
                          arrow
                          placement="top"
                          PopperProps={{
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -9], // Adjusts tooltip closer to the icon
                                },
                              },
                            ],
                          }}
                        >
                          <span>
                            <IconButton
                              className="border bg-white p-1.5"
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "8px",
                                borderColor: "#919EAB52",
                              }}
                              sx={{
                                border: "1px solid #D1D5DB",
                                borderRadius: 1,
                                padding: "4px",
                                bgcolor: isPlanReadOnly ? "transparent" : undefined,
                                opacity: isPlanReadOnly ? 0.5 : 1,
                                pointerEvents: isPlanReadOnly ? "none" : "auto",
                              }}
                              disabled={isPlanReadOnly}
                              onClick={handleCopyDataOpen}
                            >
                              <ReadMoreIcon
                                className="text-[#212B36]"
                                fontSize="small"
                                style={{
                                  height: "18px",
                                  width: "18px",
                                  color: isPlanReadOnly ? "#BDBDBD" : "#212B36",
                                }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    {shouldShowInline("compare") && (
                      <div className="flex items-center">
                        <span>
                          <IconButton
                            className="border border-gray-200 bg-gray-100 p-1.5"
                            style={{
                              width: "",
                              height: 30,
                              borderRadius: "8px",
                              borderColor: "#919EAB52",
                            }}
                            sx={{
                              border: "1px solid #D1D5DB",
                              borderRadius: 1,
                              padding: "4px",
                              bgcolor: isPlanReadOnly ? "transparent" : undefined,
                              opacity: isPlanReadOnly ? 0.5 : 1,
                              pointerEvents: isPlanReadOnly ? "none" : "auto",
                            }}
                            disabled={isPlanReadOnly}
                            onClick={handleOpenDialog}
                          >
                            <Icon
                              icon="solar:graph-up-bold-duotone"
                              width={18}
                              height={18}
                              style={{ color: isPlanReadOnly ? "#BDBDBD" : "#212B36" }}
                              className="text-gray-500"
                            />
                            <span className="text-[13px] font-bold ms-1 text-[#212B36]">
                              Compare
                            </span>
                          </IconButton>
                        </span>
                          {headerComparisonLabels?.length > 0 && (
                            <Tooltip
                              title={
                                <div>
                                  {headerComparisonLabels.map((l, i) => (
                                    <div key={`cmp-tt-${i}`} className="text-[11px] leading-[16px]">
                                      <b>{l.name}</b>: {l.summary}
                                    </div>
                                  ))}
                                </div>
                              }
                              arrow
                            >
                              <div className="ml-1 text-[10px] leading-[14px] text-[#637381] w-[76%] flex items-center gap-1">
                                <span className="truncate">
                                  <b>{headerComparisonLabels[0].name}</b>: {headerComparisonLabels[0].summary}
                                </span>
                                {headerComparisonLabels.length > 1 && (
                                  <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-[#919EAB29] text-[#212B36] w-4 h-4 text-[9px] leading-none">
                                    +{headerComparisonLabels.length - 1}
                                  </span>
                                )}
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                    {windowWidth <= 1366 && (
                    <div className="relative">
                      <IconButton
                      onClick={handleOpenResponsiveMenu}
                      size="small"
                      className={`hover:!bg-[#EDEFF2] !p-1.5 `}
                      ref={(node) => {
                        // Store a reference to the button element
                        if (node) {
                          node._buttonRef = true;
                        }
                      }}
                      style={{ backgroundColor: anchorElResponsive ? '#EDEFF2' : 'transparent' }}
                    >
                      <Icon
                        icon="solar:menu-dots-bold-duotone"
                        width={18}
                        height={18}
                      />
                      </IconButton>
                      
                      <Menu
                        anchorEl={anchorElResponsive}
                        open={Boolean(anchorElResponsive)}
                        onClose={handleCloseResponsiveMenu}
                        sx={{
                          "& .MuiMenu-list": {
                            padding: "8px",
                          },
                        }}
                      >
                        {!shouldShowInline("planStatus") && (
                          <MenuItem
                            key="plan-status"
                            onClick={handleCloseResponsiveMenu}
                            className="!p-0 !pb-2 hover:!bg-transparent"
                          >
                            <Box
                              sx={{
                                "& .MuiChip-root": {
                                  borderRadius: "6px",
                                  fontWeight: 700,
                                },
                              }}
                            >
                                <Chip
                                  size="small"
                                  sx={{
                                    ...style,
                                    "&:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                                    "&.MuiChip-clickable:hover": { backgroundColor: style?.backgroundColor || "transparent" },
                                  }}
                                  label={`${
                                    planDetails?.planStatus[0].toUpperCase() +
                                    planDetails?.planStatus.substring(1)
                                  }`}
                                />
                            </Box>
                          </MenuItem>
                        )}

                        {!shouldShowInline("asOnDate") && planDetails?.asOnDate && (
                          <MenuItem
                            key="as-on-date"
                            onClick={handleCloseResponsiveMenu}
                            className="!p-0 !pb-2 hover:!bg-transparent !pointer-events-none"
                          >
                            <Box
                       sx={{
                           "& .MuiChip-root": {
                               backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                               color: planDetails?.completedDateNumber ? "#118D57" : getDueDateStyles(planDetails?.asOnDate).color,
                               borderRadius: "6px",
                               fontWeight: "700",
                               cursor: "pointer"
                           },
                       }}
                       >
                       {planDetails?.completedDateNumber ? 
                           <Tooltip
                                  title={tooltipContent}
                                  arrow
                                  placement="top"
                                  slotProps={{
                                  tooltip: {
                                      sx: {
                                      maxWidth: 165,
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                      },
                                  },
                                  }}
                                  PopperProps={{
                                  modifiers: [
                                      {
                                      name: "offset",
                                      options: {
                                          offset: [0, -5],
                                      },
                                      },
                                  ],
                                  }}
                              >
                                  <Chip
                                      size="small"
                                      className=""
                                      onClick={() => handleDueDateClick()} 
                                      // onClick={handleVersionClick}
                                      label={planDetails?.completedDateNumber ? "Completed" : `Due ${DateFormatter({ value: planDetails?.asOnDate, format: "MMM d, yyyy", outputFormat: "string", }) || "N/A"}`}
                                      icon={
                                      planDetails?.completedDateNumber ? null :
                                      //     <AccessAlarmIcon
                                      //     width={"16px"}
                                      //     height={"16px"}
                                      //     color={getDueDateStyles(planDetails?.asOnDate).color}
                                      //     className='!w-4 !h-4'
                                      // />
                                        <Icon
                                          icon="solar:alarm-bold-duotone"
                                          width={16}
                                          height={16}
                                          color={getDueDateStyles(planDetails?.asOnDate).color}
                                        />
                                      }
                                      sx={{
                                          ".MuiChip-label": {
                                              paddingLeft : "6px",
                                              paddingRight: "6px"
                                          },
                                          "&:hover": {
                                            backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                          },
                                          "&.MuiChip-clickable:hover": {
                                            backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                          },
                                      }}
                                  />
                              </Tooltip>
                                    : 
                                    <Chip
                                            size="small"
                                            className=""
                                            onClick={() => handleDueDateClick()} 
                                            // onClick={handleVersionClick}
                                            label={`Due ${DateFormatter({ value: planDetails?.asOnDate, format: "MMM d, yyyy", outputFormat: "string", }) || "N/A"}`}
                                            icon={
                                            //     <AccessAlarmIcon
                                            //     width={"16px"}
                                            //     height={"16px"}
                                            //     color={getDueDateStyles(planDetails?.asOnDate).color}
                                            //     className='!w-4 !h-4'
                                            // />
                                              <Icon
                                                icon="solar:alarm-bold-duotone"
                                                width={16}
                                                height={16}
                                                color={getDueDateStyles(planDetails?.asOnDate).color}
                                              />
                                            }
                                            sx={{
                                                ".MuiChip-label": {
                                                    paddingLeft : "6px",
                                                    paddingRight: "6px"
                                                },
                                                "&:hover": {
                                                  backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                                },
                                                "&.MuiChip-clickable:hover": {
                                                  backgroundColor: planDetails?.completedDateNumber ? "#22C55E29" : getDueDateStyles(planDetails?.asOnDate).backgroundColor,
                                                },
                                            }}
                                        />
                                    }
                                </Box>
                          </MenuItem>
                        )}

                        {/* {!shouldShowInline("revenueType") && (
                          <MenuItem
                            key="revenue-type"
                            onClick={handleCloseResponsiveMenu}
                            className="!p-0 !pb-2"
                          >
                            <Box
                              sx={{
                                "& .MuiChip-root": {
                                  backgroundColor: "#2a5ba229",
                                  color: "#0F3C7E",
                                  borderRadius: "6px",
                                   fontWeight: "700"
                                },
                              }}
                            >
                              <Chip
                               sx={{
                                "& .MuiChip-label": {
                                  paddingLeft: "6px",
                                  paddingRight: "6px",
                                },
                              }}
                                size="small"
                                label={`${
                                  planDetails?.revenueType[0].toUpperCase() +
                                  planDetails?.revenueType.substring(1)
                                }`}
                              />
                            </Box>
                          </MenuItem>
                        )} */}

                        {!shouldShowInline("readOnly") &&
                         ( planDetails?.readOnly || isReadOnly)  && (
                            <MenuItem key="read-only" onClick={handleCloseResponsiveMenu} className="!p-0 !mb-2 hover:!bg-transparent">
                                <Box
                              sx={{
                                "& .MuiChip-root": {
                                  backgroundColor: "#919EAB29",
                                  color: "#637381",
                                  borderRadius: "6px",
                                  fontWeight: "700"
                                },
                                "& .MuiChip-root:hover, & .MuiChip-root.MuiChip-clickable:hover": {
                                  backgroundColor: "#919EAB29",
                                },
                              }}
                            >
                              <Chip
                                size="small"
                                label={"Read only"}
                                disabled={isPlanReadOnly}
                                sx={{ "&:hover": { backgroundColor: "#919EAB29" }, "&.MuiChip-clickable:hover": { backgroundColor: "#919EAB29" } }}
                              />
                            </Box>
                            </MenuItem>
                          )}

                        {/* {!shouldShowInline("autoRefresh") && (
                          <MenuItem
                            key="auto-refresh"
                            onClick={handleCloseResponsiveMenu}
                            className="!p-0 !pb-2"
                          >
                            <div className="flex items-center gap-[6px]">
                              <div
                                className={`w-[10px] h-[10px] !m-[2px] ${
                                  planDetails?.autoRefreshRevDaily
                                    ? "bg-[#22C55E]"
                                    : "bg-[#c53d22]"
                                } rounded-full`}
                              />
                              <div className="text-[12px] leading-[20px] 2xl:text-[13px]">
                                {planDetails?.autoRefreshRevDaily
                                  ? "Auto refresh rev on"
                                  : "Auto refresh rev off"}
                              </div>
                            </div>
                          </MenuItem>
                        )} */}
                        <hr className="px-1.5" />
                        {!shouldShowInline("settings") && planDetails && (
                          <MenuItem
                            key="settings"
                            onClick={() => {
                              handleVersionClick();
                              handleCloseResponsiveMenu();
                            }}
                            className="!p-0 !py-2 hover:!bg-transparent w-full"
                          >
                            <>
                                <IconButton 
                                  className="bg-white p-1.5 w-full !justify-start hover:!bg-[#EDEFF2]"
                                  style={{
                                    width: "",
                                    height: 30,
                                    borderRadius: "8px",
                                    borderColor: "#919EAB52",
                                  }}
                                  sx={{
                                    // border: "1px solid #D1D5DB",
                                    borderRadius: 1,
                                    padding: "4px",
                                    opacity: isPermissionReadOnly ? 0.5 : 1,
                                    pointerEvents: isPermissionReadOnly ? "none" : "auto",
                                  }}
                                  onClick={handleVersionClick}
                                  disabled={isPermissionReadOnly}
                                >
                                  <Icon
                                    icon="solar:settings-bold-duotone"
                                    width={18}
                                    height={18}
                                    style={{ color: isPermissionReadOnly ? "#BDBDBD" : "#212B36" }}
                                    className=""
                                  />
                                  <span className="text-[13px] font-bold ms-1 !text-[#212B36]">
                                    Version settings
                                  </span>
                                </IconButton>
                            </>
                          </MenuItem>
                        )}

                        {showLastClosedMonthInHeader && !shouldShowInline("settings") && (
                          <MenuItem
                            key="lastClosedMonth"
                            className="!p-0 !mb-2 hover:!bg-transparent w-full !cursor-default"
                            onClick={(e) => e.stopPropagation()}
                            disableRipple
                          >
                            <Tooltip
                              title={
                                isClosedMonthLoading
                                  ? "Loading last closed month..."
                                  : closedMonthLabel
                                    ? isClosedMonthStale
                                      ? closedMonthStaleTooltipTitle
                                      : `Last closed month: ${closedMonthLabel}`
                                    : "Last closed month: Not available"
                              }
                              arrow
                              placement="bottom"
                              slotProps={{
                                tooltip: {
                                  sx: { fontSize: "12px", maxWidth: 320 },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                  width: "100%",
                                  borderRadius: "8px",
                                  backgroundColor: isClosedMonthWarning ? "#FFF7E6" : "#E6FCFF",
                                  color: isClosedMonthWarning ? "#7A4100" : "#003768",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {isClosedMonthWarning ? (
                                  <WarningRoundedIcon sx={{ width: 18, height: 18, color: "#FFAB00" }} />
                                ) : (
                                  <InfoOutlinedIcon sx={{ width: 18, height: 18, color: "#00B8D9" }} />
                                )}
                                <span>Last closed month</span>
                              </Box>
                            </Tooltip>
                          </MenuItem>
                        )}

                        {!shouldShowInline("import-plan") && [
                          <Box
                            key="copy-data-header"
                            sx={{ px: "12px", pt: "8px", pb: "4px" }}
                          >
                            <Typography
                              className="font-['Public_Sans']"
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                lineHeight: "100%",
                                color: "#212B36",
                              }}
                            >
                              Copy data
                            </Typography>
                          </Box>,
                          <MenuItem
                            key="copy-from-plan"
                            disabled={isPlanReadOnly}
                            onClick={isPlanReadOnly ? undefined : () => {
                              handleCopyDataSelect("plan");
                              handleCloseResponsiveMenu();
                            }}
                            sx={{
                              pl: "20px",
                              fontSize: "12px",
                              fontWeight: 400,
                              lineHeight: "22px",
                              color: "#637381",
                              fontFamily: "Public Sans",
                            }}
                          >
                            From plan
                          </MenuItem>,
                          <MenuItem
                            key="copy-from-actuals"
                            disabled={isPlanReadOnly}
                            onClick={isPlanReadOnly ? undefined : () => {
                              handleCopyDataSelect("actuals");
                              handleCloseResponsiveMenu();
                            }}
                            sx={{
                              pl: "20px",
                              fontSize: "12px",
                              fontWeight: 400,
                              lineHeight: "22px",
                              color: "#637381",
                              fontFamily: "Public Sans",
                            }}
                          >
                            From actuals
                          </MenuItem>,
                        ]}

                        {!shouldShowInline("compare") && ([
                          <MenuItem
                            key="compare"
                            disabled={isPlanReadOnly}
                            onClick={isPlanReadOnly ? undefined : () => {
                              handleOpenDialog();
                              handleCloseResponsiveMenu();
                            }}
                            className="!p-0 !mb-2 hover:!bg-transparent w-full"
                          >
                            <div className="flex flex-col gap-2 w-full">
                              <IconButton
                                className="bg-gray-100 p-1.5 w-full !justify-start hover:!bg-[#EDEFF2]"
                                style={{
                                  width: "",
                                  height: 30,
                                  borderRadius: "8px",
                                  borderColor: "#919EAB52",
                                }}
                                sx={{
                                  // border: "1px solid #D1D5DB",
                                  borderRadius: 1,
                                  padding: "4px",
                                  opacity: isPlanReadOnly ? 0.5 : 1,
                                  pointerEvents: isPlanReadOnly ? "none" : "auto",
                                }}
                                onClick={handleOpenDialog}
                                disabled={isPlanReadOnly}
                              >
                                <Icon
                                  icon="solar:graph-up-bold-duotone"
                                  width={18}
                                  height={18}
                                  style={{ color: isPlanReadOnly ? "#BDBDBD" : "#212B36" }}
                                />
                                <span className="text-[13px] font-bold ms-1 text-[#212B36]">
                                  Compare
                                </span>
                              </IconButton>
                            </div>
                          </MenuItem>,
                          <MenuItem key="actual-description" className="!px-1 hover:!bg-transparent pointer-events-none">
                          {headerComparisonLabels?.length > 0 && (
                                <div className="text-[10px] leading-[14px] text-[#637381] max-w-[220px] break-words whitespace-normal">
                                  {headerComparisonLabels.map((l, i) => (
                                    <div key={`cmp-menu-${i}`} className="mb-0.5">
                                      <b>{l.name}</b>: {l.summary}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </MenuItem>
                          ])}
                      </Menu>
                    </div>
                  )}
                </Box>
                {/* Consolidated dialogs - only render when their open flags are true */}
                {openVersionModal && planDetails && (
                  <PlanVersionModal
                    isOpen={openVersionModal}
                    setOpenVersionModal={setOpenVersionModal}
                    planParams={planDetails}
                    setCreateNewPlan={setCreateNewPlan}
                    pageSpecificLoader={loading}
                    setLoading={setLoading}
                    isNewVersion={isNewVersion}
                    planDetailsRowData={planDetailsRowData}
                    showToast={showToast}
                    setUpdatePlanDetailsPage={setRefreshPageData}
                    modalOpenedFrom={modalOpenedFrom}
                  />
                )}
                {openCreateVersion && planDetails && (
                  <PlanVersionModal
                    isOpen={openCreateVersion}
                    setOpenVersionModal={setOpenCreateVersion}
                    planParams={planDetails}
                    setCreateNewPlan={setCreateNewPlan}
                    pageSpecificLoader={loading}
                    isNewVersion={isNewVersion}
                    setLoading={setLoading}
                    planDetailsRowData={planDetailsRowData}
                    showToast={showToast}
                    modalOpenedFrom={modalOpenedFrom}
                  />
                )}
                {isDialogOpen && (
                  <ConfigureComparisonDialog
                    open={isDialogOpen}
                    handleClose={handleCloseDialog}
                    planId={id}
                    setUpdatePlanDetailsPage={setRefreshPageData}
                    showToast={showToast}
                    setShowVarianceInHighlights={setShowVarianceInHighlights}
                    setShowVarianceInRollups={setShowVarianceInRollups}
                    planDetails={planDetails}
                  />
                )}
                {openImportActualModal && (
                  <ImportActualModal
                    isOpen={openImportActualModal}
                    setOpenImportActualModal={setOpenImportActualModal}
                    isSingle={true}
                    rowsData={planDetails ? [planDetails] : []}
                    setLoading={setLoading}
                    showToast={showToast}
                    mode={importMode}
                    onClose={() => {}}
                    onJobComplete={handleImportJobComplete}
                  />
                )}
                <Menu
                  anchorEl={copyDataAnchor}
                  open={Boolean(copyDataAnchor)}
                  onClose={handleCopyDataClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      minWidth: 119,
                      borderRadius: "4px",
                      padding: "8px",
                      boxShadow:
                        "-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D",
                      "& .MuiMenu-list": {
                        padding: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      },
                    },
                  }}
                >
                  <Typography
                    className="font-['Public_Sans']"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      lineHeight: "100%",
                      color: "#212B36",
                      px: "8px",
                      py: "4px",
                    }}
                  >
                    Copy data
                  </Typography>
                  <MenuItem
                    onClick={() => handleCopyDataSelect("plan")}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "22px",
                      color: "#637381",
                      pl: "34px",
                      pr: "8px",
                      py: "2px",
                      minHeight: 0,
                      fontFamily: "Public Sans",
                    }}
                  >
                    From plan
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleCopyDataSelect("actuals")}
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "22px",
                      color: "#637381",
                      pl: "34px",
                      pr: "8px",
                      py: "2px",
                      minHeight: 0,
                      fontFamily: "Public Sans",
                    }}
                  >
                    From actuals
                  </MenuItem>
                </Menu>
                {markCompleteModalOpen && (
                  <MarkCompleteDialog
                    isOpen={markCompleteModalOpen}
                    setOpenMultipleMarkCompelte={setMarkCompleteModalOpen}
                    rowData={planDetails} 
                    showToast={showToast}
                    onSuccessfulUpdate={() => fetchHeaderData(true)}
                  />
                )}
                </Box>
              )
            ) : (
              <h1 className="text-2xl leading-[36px] text-[#212B36] font-bold flex-shrink-0">
                {(() => {
                  const pageTitle = getPageTitle(location.pathname);

                  // If title is null, it means it's dynamic
                  if (pageTitle === null) {
                    return null;
                  }

                  return pageTitle;
                })()}
              </h1>
            )
            }
            {/* discard/Save button changes */}
            <div
              className={`ml-auto cursor-default ${
                (isFeatureEnabled(location.pathname, 'showSaveButtons') || isFeatureEnabled(location.pathname, 'showUserAvatars')) 
                  ? "flex items-center" 
                  : " "
              }`}
            >
              {isCritiqueDetails ? (
                <div className="flex items-center cursor-default" style={{ gap: "8px" }}>
                  {!critiqueDetailsData ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexShrink: 0,
                      }}
                    >
                      <Skeleton variant="rounded" width={88} height={30} sx={{ borderRadius: "8px" }} />
                      <Skeleton variant="rounded" width={30} height={30} sx={{ borderRadius: "8px" }} />
                      <Skeleton variant="rounded" width={118} height={30} sx={{ borderRadius: "8px" }} />
                      <Skeleton variant="rounded" width={52} height={30} sx={{ borderRadius: "8px" }} />
                      <Skeleton variant="rounded" width={62} height={30} sx={{ borderRadius: "8px" }} />
                    </Box>
                  ) : (
                    <>
                  {/* Autosave animation — shown when background draft save is in progress (order per design: Autosaved first) */}
                  {draftSaveStatus && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {draftSaveStatus === "saving" ? (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              "@keyframes critique-details-draft-spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                              animation: "critique-details-draft-spin 1s linear infinite",
                            }}
                          >
                            <AutorenewIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                          </Box>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, fontSize: "13px", lineHeight: "22px", color: "#212B36" }}
                          >
                            Autosaving
                          </Typography>
                        </>
                      ) : draftSaveStatus === "saved" ? (
                        <>
                          <CheckCircleIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, fontSize: "13px", lineHeight: "22px", color: "#212B36" }}
                          >
                            Autosaved
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  )}
                   {/* <div className="flex items-center space-x-0.5">
                     {displayedUsers.map((user, index) => (
                      <Tooltip key={index} title={user.fullName || "user"} arrow placement="top" slotProps={{ tooltip: { sx: { cursor: "default" } } }}>
                        <div key={user.id} className="relative inline-block">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center lowercase text-white font-medium text-xs cursor-default" style={{ backgroundColor: generateColor(user.fullName || "user") }}>
                            {getInitials(user.fullName)}
                          </div>
                          <StatusDot active={user.active} />
                        </div>
                      </Tooltip>
                    ))}
                    {remainingCount > 0 && (
                      <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                        <Tooltip title={<OverflowTooltipContent remainingUsers={remainingUsers} />} arrow placement="bottom-end" disableHoverListener disableFocusListener disableTouchListener open={tooltipOpen} onClose={() => setTooltipOpen(false)} slotProps={{ tooltip: { sx: { bgcolor: "white", border: "1px solid #E2E5E970", borderRadius: "4px", maxHeight: "200px", minWidth: "170px", boxShadow: "-20px 20px 40px 4px #919EAB3D" } } }}>
                          <span className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-[#637381] font-medium text-xs hover:scale-105 transition-transform cursor-pointer" onClick={() => setTooltipOpen(!tooltipOpen)}>+{remainingCount}</span>
                        </Tooltip>
                      </ClickAwayListener>
                    )}
                  </div> */}
                  {renderActiveUserAvatars()}
                  <Tooltip title={critiqueExportPdfTooltip} arrow placement="bottom">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canExportCritiquePdf}
                        onClick={() => setCritiqueExportPDFModalOpen(true)}
                        sx={{
                          width: 30,
                          height: 30,
                          border: "1px solid #919EAB52",
                          borderRadius: "8px",
                          "&:hover": { bgcolor: "rgba(145, 158, 171, 0.08)" },
                          "&.Mui-disabled": { opacity: 0.5 },
                        }}
                        aria-label="Export to PDF"
                      >
                        <GetAppIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* <IconButton
                    size="small"
                    disabled
                    sx={{
                      width: 30,
                      height: 30,
                      disabled: true,
                      border: "1px solid #919EAB52",
                      borderRadius: "8px",
                      "&:hover": { bgcolor: "rgba(145, 158, 171, 0.08)" },
                    }}
                    aria-label="Export"
                  >
                    <GetAppIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                  </IconButton> */}
                  {/* Discard changes — shown when draft exists */}
                  {showHeaderBtn && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={critiqueDetailsData?.is_completed === true || !critiqueComment}
                      className="!text-[#FF5630] !border-[#FF56307A] hover:!border-[#ff5630] hover:!bg-[#FFF1EE] !px-2 !py-[2.63px] leading-[22px] !font-bold !text-[13px] !text-nowrap"
                      sx={{
                        border: "1px solid #FF563099",
                        color: "#FF5630",
                        "&:hover": {
                          borderColor: "#FF5630",
                        },
                        "&.Mui-disabled": { opacity: 0.45 },
                      }}
                      onClick={() => setCritiqueDetailsDiscardDialogOpen(true)}
                    >
                      Discard changes
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={critiqueDetailsData?.is_completed === true || !isEdited || !critiqueComment}
                    className="!px-4"
                    sx={{
                      height: "30px",
                      fontFamily: "Public Sans",
                      fontWeight: 700,
                      fontSize: "13px",
                      lineHeight: "22px",
                      letterSpacing: 0,
                      border: "1px solid var(--components-button-outlined, #919EAB52)",
                      color: "#212B36",
                      "&:hover": {
                        backgroundColor: "#F3F4F6",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.45,
                        color: "#919EABCC",
                        border: "1px solid var(--action-disabled-bg, #919EAB33)",
                        cursor: "not-allowed",
                      },
                    }}
                    onClick={() => window.dispatchEvent(new CustomEvent("critique-details-save"))}
                  >
                    Save
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={critiqueDetailsData?.is_completed === true || !critiqueSubmit}
                    className="!px-4"
                    sx={{
                      height: "30px",
                      fontFamily: "Public Sans",
                      fontWeight: 700,
                      fontSize: "13px",
                      lineHeight: "22px",
                      letterSpacing: 0,
                      backgroundColor: "#212B36",
                      color: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#161C24",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.45,
                        color: "#919EABCC",
                        backgroundColor: "#919EAB33",
                        cursor: "not-allowed",
                      },
                    }}
                    onClick={() => window.dispatchEvent(new CustomEvent("critique-details-submit-request"))}
                  >
                    Submit
                  </Button>
                    </>
                  )}
                </div>
              ) : (isFeatureEnabled(location.pathname, 'showSaveButtons') || isFeatureEnabled(location.pathname, 'showUserAvatars')) && !isCritiqueTemplateEdit ? (
                <div className="flex items-center cursor-default gap-2">
                  {pageType === "coa" && draftSaveStatus && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                      }}
                    >
                      {draftSaveStatus === "saving" ? (
                        <>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              "@keyframes coa-draft-spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                              animation: "coa-draft-spin 1s linear infinite",
                            }}
                          >
                            <AutorenewIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                          </Box>
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, fontSize: "13px", lineHeight: "22px", color: "#212B36" }}
                          >
                            Autosaving
                          </Typography>
                        </>
                      ) : draftSaveStatus === "saved" ? (
                        <>
                          <CheckCircleIcon sx={{ width: 18, height: 18, color: "#212B36" }} />
                          <Typography
                            component="span"
                            sx={{ fontWeight: 700, fontSize: "13px", lineHeight: "22px", color: "#212B36" }}
                          >
                            Autosaved
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  )}
                  {isFeatureEnabled(location.pathname, 'showUserAvatars') && renderActiveUserAvatars()}
                  {(showHeaderBtn || (showCoaHeaderBtn && isFeatureEnabled(location.pathname, 'showDiscardButton'))) && (windowWidth > 1365 ? (
                    <Button
                      variant="outlined"
                       color="inherit"
                      size="small"
                      className="!text-[#FF5630] !border-[#FF56307A] hover:!border-[#ff5630] hover:!bg-[#FFF1EE] !px-2 !py-[2.63px] leading-[22px] !font-bold !text-[13px] !text-nowrap"
                      sx={{
                        border: "1px solid #FF563099",
                        ":hover": {
                          borderColor: "#FF5630",
                        },
                        color: (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630", // Lighter color when disabled
                        borderColor: (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630", // Lighter border color
                        ":hover": {
                          borderColor: (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630", // Hover effect for disabled state
                        },
                        pointerEvents: (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "none" : "auto", // Disable pointer events when disabled
                        opacity: (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? 0.5 : 1, // Reduce opacity for disabled look
                      }}
                      onClick={() => {
                        setCurrentModalName("Discard change");
                        if (pageType === 'coa') {
                          setCoaDiscardDialogOpen(true);
                        } else {
                          setDiscardDialogOpen(true);
                        }
                      }}
                      disabled={pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly}
                    >
                      Discard changes
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                       color="inherit"
                      size="small"
                      className="!text-[#FF5630] !border-[#FF56307A] hover:!border-[#ff5630] hover:!bg-[#FFF1EE] 2xl:!text-[13px] !text-[10px] !text-nowrap !p-0 !min-w-fit w-[30px] h-[30px]"
                      sx={{
                        border: `1px solid ${!(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630"}`,
                        color: !(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630",
                        ":hover": {
                          borderColor: !(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "#BDBDBD" : "#FF5630",
                        },
                        opacity: !(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? 0.5 : 1,
                        pointerEvents: !(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly) ? "none" : "auto",
                      }}
                      disabled={!(showHeaderBtn || showCoaHeaderBtn)|| (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly)}
                      onClick={() => {
                        if (pageType === 'coa') {
                          setCoaDiscardDialogOpen(true);
                          setCurrentModalName("Discard change");
                        } else {
                          setDiscardDialogOpen(true);
                          setCurrentModalName("Discard change");
                        }
                      }}
                    >
                      <Icon
                        icon="solar:trash-bin-trash-bold"
                        width={18}
                        height={18}
                      />
                    </Button>
                  ))}
                  {isDiscardDialogOpen && (
                    <DiscardChangesDialog
                      isOpen={isDiscardDialogOpen}
                      setOpenDiscardChangesModal={setDiscardDialogOpen}
                      planName={planDetails?.versionName}
                      onClose={handleCancelDiscard}
                      onSubmit={handlePlanDiscardClick}
                    />
                  )}
                  {isCoaDiscardDialogOpen && (
                    <DiscardChangesDialog
                      isOpen={isCoaDiscardDialogOpen}
                      setOpenDiscardChangesModal={setCoaDiscardDialogOpen}
                      onClose={handleCancelCoaDiscard}
                      onSubmit={handleCoaDiscardClick}
                    />
                  )}
                    {/* <UnDiscardChangesDialog
                    isOpen={isUnDiscardDialogOpen}
                    setOpenUnDiscardChangesModal={setUnDiscardDialogOpen}
                    onClose={handleDiscardAndNavigate}
                    onSubmit={handleSaveAndNavigate}
                    onCancel={handleCancelUnDiscardDialog}
                  /> */}
                  <div className="relative flex items-center">
                    <ButtonGroup
                      variant="contained"
                      className="border-r border-[#f4f6f8] [&_.MuiButtonGroup-grouped]:!min-w-[24px] !shadow-none"
                    >
                      {/* Tooltip for the Save button */}
                      {!(pageType === 'coa' && showCoaHeaderBtn) ? 
                      <Tooltip
                        title="Save current version"
                        arrow
                        placement="top"
                      >
                        <span>
                          <Button
                            variant="contained"
                            className={`${
                              !(showHeaderBtn || showCoaHeaderBtn)
                                ? "!bg-[#E9ECEE] !text-[#A1ACB7]"
                                : ""
                            } ${pageType === 'coa' ? "!rounded-[8px]" : ""} !text-[12px] border-r  !border-[#f4f6f8] !px-4 !py-[4.5px]`}
                            sx={{
                              backgroundColor: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#212B36",
                              color: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#FFFFFF",
                              "&:hover": {
                                backgroundColor: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#161C24",
                              },
                            }}
                            onClick={handleSaveClick}
                            disabled={!(showHeaderBtn || (showCoaHeaderBtn && isFeatureEnabled(location.pathname, 'showDiscardButton'))) || (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly)}
                          >
                            Save
                          </Button>
                        </span>
                      </Tooltip>
                      : 
                      <>
                        <Button
                            variant="contained"
                            className={`${
                              !(showHeaderBtn || showCoaHeaderBtn)
                                ? "!bg-[#E9ECEE] !text-[#A1ACB7]"
                                : ""
                            } ${pageType === 'coa' ? "!rounded-[8px]" : ""} !text-[12px] border-r  !border-[#f4f6f8] !px-4 !py-[4.5px]`}
                            sx={{
                              backgroundColor: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#212B36",
                              color: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#FFFFFF",
                              "&:hover": {
                                backgroundColor: !(showHeaderBtn || showCoaHeaderBtn) ? undefined : "#161C24",
                              },
                            }}
                            onClick={handleSaveClick}
                            disabled={!(showHeaderBtn || showCoaHeaderBtn) || (pageType === 'coa' ? isCoaReadOnly : isPlanReadOnly)}
                          >
                            Save
                          </Button>
                          </>
                      }
                      <Tooltip
                        title="Save as new version"
                        arrow
                        placement="top"
                        className={`${
                          !isFeatureEnabled(location.pathname, "showSaveAsNewButton") ||
                          saveAsNewVersionHidden
                            ? "hidden"
                            : "inline-block"
                        }`}
                      >
                        <span>
                          <Button
                            className={`!w-[24px] ${
                               showHeaderBtn &&  (!(showHeaderBtn || showCoaHeaderBtn))
                                ? "!bg-[#E9ECEE] !text-[#A1ACB7] !p-0 !m-0"
                                : "!p-0 !m-0 !h-full"
                            }`}
                            onClick={handleSaveAsNewVersionClick}
                            sx={{
                              backgroundColor: showHeaderBtn && (!(showHeaderBtn || showCoaHeaderBtn)) ? undefined : "#212B36",
                              color: showHeaderBtn && (!(showHeaderBtn || showCoaHeaderBtn)) ? undefined : "#FFFFFF",
                              "&:hover": {
                                backgroundColor: showHeaderBtn && (!(showHeaderBtn || showCoaHeaderBtn)) ? undefined : "#161C24",
                              },
                              height: "100%"
                            }}
                            disabled={
                              (showHeaderBtn &&
                                !(showHeaderBtn || (showCoaHeaderBtn && pageType === "coa"))) ||
                              (pageType === "coa" ? isCoaReadOnly : isPlanReadOnly) ||
                              saveAsNewVersionDisabled
                            }
                          >
                            <Icon
                              icon="solar:alt-arrow-down-line-duotone"
                              fontSize="medium"
                              className=""
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    </ButtonGroup>
                    {/* <SaveAsNewDialog
                isOpen={isSaveAsNewDialogOpen} 
                setOpenSaveAsNewModal={setSaveAsNewDialogOpen}
                onClose={()=>{
                  console.log('dialog closed!');
                  
                }}
                onSubmit={()=>{
                  console.log('save as new clicked!');
                }} 
                /> */}
                  </div>
                </div>
              ) : (
                <></>
              )}
                    {isCritiqueTemplateEdit && draftSaveStatus && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginRight: "16px",
                        }}
                      >
                        {draftSaveStatus === "saving" ? (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                "@keyframes critique-draft-spin": {
                                  "0%": { transform: "rotate(0deg)" },
                                  "100%": { transform: "rotate(360deg)" },
                                },
                                animation: "critique-draft-spin 1s linear infinite",
                              }}
                            >
                              <AutorenewIcon
                                sx={{
                                  width: 18,
                                  height: 18,
                                  color: "#212B36",
                                }}
                              />
                            </Box>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: "13px",
                                lineHeight: "22px",
                                color: "#212B36",
                              }}
                            >
                              Autosaving
                            </Typography>
                          </>
                        ) : draftSaveStatus === "saved" ? (
                          <>
                            <CheckCircleIcon sx={{ width: 18, height: 18, color: "212B36" }} />
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 700,
                                fontSize: "13px",
                                lineHeight: "22px",
                                color: "#212B36",
                              }}
                            >
                              Autosaved
                            </Typography>
                          </>
                        ) : null}
                      </Box>
                    )}
              {/* Critique template save buttons are handled in CritiqueTemplateDetailsHeader */}
              {isCritiqueTemplateEdit && (
                loading ? (
                  <div className="relative flex items-center" style={{ gap: "8px" }}>
                    <Skeleton variant="rounded" width={120} height={30} />
                    <Skeleton variant="rounded" width={140} height={30} />
                  </div>
                ) : (
                  <div className="relative flex items-center" style={{ gap: "16px" }}>
                    {showHeaderBtn && (
                      <Button
                        variant="outlined"
                        size="small"
                        className="!text-[#FF5630] !border-[#FF56307A] hover:!border-[#ff5630] hover:!bg-[#FFF1EE] !px-2 !py-[2.63px] leading-[22px] !font-bold !text-[13px] !text-nowrap"
                        sx={{
                          border: "1px solid #FF563099",
                          color: "#FF5630",
                          ":hover": {
                            borderColor: "#FF5630",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.45,
                            borderColor: "#919EAB52",
                            color: "#919EABCC",
                          },
                        }}
                        onClick={() => setCritiqueDiscardDialogOpen(true)}
                        disabled={!critiqueTemplateEdit}
                      >
                        Discard changes
                      </Button>
                    )}
                    <ButtonGroup
                      variant="contained"
                      className="[&_.MuiButtonGroup-grouped]:!min-w-0 !shadow-none"
                      sx={{
                        height: "30px",
                        borderRadius: "8px",
                        gap: "1px",
                        overflow: "hidden",
                        "& .MuiButtonGroup-grouped": {
                          border: "none",
                          "&:not(:last-of-type)": {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            borderRight: "1px solid rgba(224, 224, 224, 0.8)",
                          },
                          "&:last-of-type": {
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            minWidth: 32,
                            padding: 0,
                          },
                        },
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        className={`${
                          !showHeaderBtn ? "!bg-[#919EAB33] !text-[#919EABCC]" : "!bg-[#212B36] !text-[#FFFFFF] hover:!bg-[#212B36]"
                        } !px-4 !text-[12px] !font-bold`}
                        sx={{
                          height: "30px",
                          backgroundColor: !showHeaderBtn ? "#919EAB33" : "#212B36",
                          color: !showHeaderBtn ? "#919EABCC" : "#FFFFFF",
                          "&:hover": {
                            backgroundColor: !showHeaderBtn ? "#919EAB33" : "#161C24",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.45,
                            backgroundColor: "#919EAB33",
                            color: "#919EABCC",
                          },
                        }}
                        onClick={handleCritiqueSave}
                        disabled={!showHeaderBtn || !critiqueTemplateEdit}
                      >
                        Save
                      </Button>
                      <Button
                        className="!bg-[#E9ECEE] !text-[#919EABCC] !p-0"
                        sx={{
                          height: "30px",
                          width:"24px",
                          backgroundColor: "#E9ECEE",
                          color: "#919EABCC",
                          cursor: "default",
                          pointerEvents: "none",
                          "&:hover": {
                            backgroundColor: "#E9ECEE",
                          },
                        }}
                        disabled
                        aria-label="Save options (disabled)"
                      >
                        <Icon
                          icon="solar:alt-arrow-down-line-duotone"
                          fontSize="medium"
                          width={18}
                          height={18}
                          sx={{ width: 18, height: 18 }}
                        />
                      </Button>
                    </ButtonGroup>
                  </div>
                )
              )}
              {isCritiqueDiscardDialogOpen && (
                <DiscardChangesDialog
                  isOpen={isCritiqueDiscardDialogOpen}
                  setOpenDiscardChangesModal={setCritiqueDiscardDialogOpen}
                  planName={templateData?.critique_name}
                  onClose={handleCancelCritiqueDiscard}
                  onSubmit={handleCritiqueDiscardClick}
                />
              )}
              {isCritiqueDetailsDiscardDialogOpen && (
                <DiscardChangesDialog
                  isOpen={isCritiqueDetailsDiscardDialogOpen}
                  setOpenDiscardChangesModal={setCritiqueDetailsDiscardDialogOpen}
                  planName={critiqueDetailsData?.critique_name || "Untitled Critique"}
                  onClose={handleCancelCritiqueDetailsDiscard}
                  onSubmit={handleCritiqueDetailsDiscardClick}
                />
              )}
              {critiqueExportPDFModalOpen && (
                <Suspense fallback={<LoadingScreen position="fixed" title="Loading export tools..." />}>
                  <CritiqueExportPDFModal
                    open={critiqueExportPDFModalOpen}
                    onClose={() => setCritiqueExportPDFModalOpen(false)}
                    critiqueData={critiqueDetailsData}
                  />
                </Suspense>
              )}
              {/* Save & Complete confirmation dialog */}
              <Dialog
                open={isCritiqueCompleteDialogOpen}
                onClose={() => setCritiqueCompleteDialogOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: "12px" } }}
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    px: "20px",
                    pt: "20px",
                    pb: "8px",
                  }}
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#212B36", lineHeight: "28px", pr: 2 }}>
                    Save and complete &ldquo;{critiqueDetailsData?.critique_name || "Untitled Critique"}&rdquo;
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setCritiqueCompleteDialogOpen(false)}
                    sx={{ mt: "-2px", mr: "-8px", color: "#637381", flexShrink: 0 }}
                  >
                    <ClearIcon sx={{ width: 16, height: 16 }} />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: "20px", pb: "4px" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#212B36", lineHeight: "22px" }}>
                    Once you save and complete, This critique no longer be edited.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ px: "20px", pb: "20px", gap: "8px" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    className="!border-[#919EAB52] !text-[#212B36] !text-[13px] !font-bold"
                    sx={{ height: "36px", border: "1px solid #919EAB52" }}
                    onClick={() => setCritiqueCompleteDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    className="!bg-[#212B36] !text-white hover:!bg-[#161C24] !text-[13px] !font-bold"
                    sx={{ height: "36px", backgroundColor: "#212B36", "&:hover": { backgroundColor: "#161C24" } }}
                    onClick={() => {
                      setCritiqueCompleteDialogOpen(false);
                      window.dispatchEvent(new CustomEvent("critique-details-complete"));
                    }}
                  >
                    Save &amp; complete
                  </Button>
                </DialogActions>
              </Dialog>
              <div onClick={toggleSearch}>
                <IconButton className="">
                  <SearchIcon className="text-gray-400 hover:text-blue-500 transition-colors duration-200" />
                </IconButton>
              </div>
              {isCritiqueTemplateEdit && (
                <IconButton>
                  <NotificationsIcon className="text-gray-400 hover:text-blue-500 transition-colors duration-200" />
                </IconButton>
              )}
              {isFeatureEnabled(location.pathname, 'showProfile') && (
                <div className="flex items-center ml-4">
                  <LoginButton />
                  <Profile />
                </div>
              )}
            </div>
          </div>
        </header>
      </div>
      <div className="absolute inset-x-0 bottom-[-4px] h-1 bg-gradient-to-r from-[#a4de48] to-[#5ec1b0]"></div>
    </div>)
  );
};

export default Header;
